/**
 * TUVI - Módulo Centralizado de Facturas
 * Gestión independiente de facturas con distribución multi-proyecto
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { matchesAnyField } from '../../utils/searchHelper';
import { useNotification } from '../../context/NotificationContext';
import { useAuth } from '../../context/AuthContext';
import client from '../../services/graphqlClient';
import { BACKEND_URL, UPLOAD_URL } from '../../config';
import * as XLSX from 'xlsx-js-style';
import FacturasSinClasificar from './FacturasSinClasificar';
import './Facturas.css';
import '../Proveedores/ModalDescuentoProveedor.css'; // estilos fd-* del bloque de descuentos

const CONCEPTOS_FACTURA = [
  { value: 'anticipo', label: 'Anticipo', repetible: false },
  { value: 'avance', label: 'Avance', repetible: false },
  { value: 'liquidacion', label: 'Liquidacion', repetible: false },
  { value: 'retenido', label: 'Retenido', repetible: false },
  { value: 'compra', label: 'Compra', repetible: true },
  { value: 'otro', label: 'Otro', repetible: true },
];

const Facturas = ({ tipoFactura: tipoProp }) => {
  const { success, error: showError } = useNotification();
  const { user } = useAuth();

  // Tipo de factura: 'proyecto' (normal) o 'administrativa'
  const [tipoVista, setTipoVista] = useState(tipoProp || 'proyecto');

  // Sincronizar si cambia la prop
  React.useEffect(() => { if (tipoProp) setTipoVista(tipoProp); }, [tipoProp]);

  // Data states
  const [facturas, setFacturas] = useState([]);
  const [estadisticas, setEstadisticas] = useState(null);
  const [proveedores, setProveedores] = useState([]);
  const [proyectos, setProyectos] = useState([]);
  const [conceptosBD, setConceptosBD] = useState([]); // capítulos + conceptos desde Conceptos Facturas
  const [loading, setLoading] = useState(true);

  // Filter states
  const [filtroEstado, setFiltroEstado] = useState('todos');
  const [busqueda, setBusqueda] = useState('');

  // Form states
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [facturaEditandoId, setFacturaEditandoId] = useState(null);
  const [gruposExpandidos, setGruposExpandidos] = useState({}); // { [grupo_factura_id]: true }
  // Report tabs
  const [reportTab, setReportTab] = useState(null); // null | 'vencimiento' | 'proveedor' | 'proyecto'
  const [filtroVencDesde, setFiltroVencDesde] = useState('');
  const [filtroVencHasta, setFiltroVencHasta] = useState('');
  const [filtroProvId, setFiltroProvId] = useState('');
  const [reportProvBusqueda, setReportProvBusqueda] = useState('');
  const [showReportProvDrop, setShowReportProvDrop] = useState(false);
  const [filtroProvFechaDesde, setFiltroProvFechaDesde] = useState('');
  const [filtroProvFechaHasta, setFiltroProvFechaHasta] = useState('');
  const [filtroProyId, setFiltroProyId] = useState('');
  const [reportProyBusqueda, setReportProyBusqueda] = useState('');
  const [showReportProyDrop, setShowReportProyDrop] = useState(false);
  const [filtroProyFechaDesde, setFiltroProyFechaDesde] = useState('');
  const [filtroProyFechaHasta, setFiltroProyFechaHasta] = useState('');

  // Form data
  const [formData, setFormData] = useState({
    numero_factura: '',
    fecha_emision: new Date().toISOString().split('T')[0],
    fecha_vencimiento: '',
    descripcion: '',
    categoria: '',
    proveedor_id: '',
    subtotal: '',
    porcentaje_iva: 0
  });

  // Búsqueda proveedor
  const [busquedaProveedor, setBusquedaProveedor] = useState('');
  const [showProveedorDropdown, setShowProveedorDropdown] = useState(false);
  const proveedorDropdownRef = useRef(null);

  // Validación factura duplicada
  const [facturaExiste, setFacturaExiste] = useState(false);

  // Concepto violation tracking (no-repeat concepts already used)
  const [conceptoViolaciones, setConceptoViolaciones] = useState({});

  // Multi-project distribution
  const [proyectosDistribucion, setProyectosDistribucion] = useState([]);
  const [busquedaProyecto, setBusquedaProyecto] = useState('');
  const [mostrarListaProyectos, setMostrarListaProyectos] = useState(false);

  // Posventa
  const [tabProyecto, setTabProyecto] = useState('proyectos'); // 'proyectos' | 'posventa' | 'especiales'
  // Proyectos especiales: mismo flujo que un proyecto de obra (se cargan en la
  // misma consulta, con incluir_especiales), pero en su propia pestaña.
  const [busquedaEspecial, setBusquedaEspecial] = useState('');
  const [mostrarListaEspeciales, setMostrarListaEspeciales] = useState(false);
  const [posventaList, setPosventaList] = useState([]);
  const [busquedaPosventa, setBusquedaPosventa] = useState('');
  const [mostrarListaPosventa, setMostrarListaPosventa] = useState(false);
  const [mostrarFormPosventa, setMostrarFormPosventa] = useState(false);
  const [formPosventa, setFormPosventa] = useState({ proyecto_nombre: '', apartamento: '' });

  // Retentions
  const [proveedorSeleccionado, setProveedorSeleccionado] = useState(null);
  const [calculos, setCalculos] = useState({
    iva: 0, monto_total: 0, retencion_fuente: 0, retencion_iva: 0,
    total_retenciones: 0, valor_neto_pagar: 0
  });

  // Payment plan
  const [planPagosActivo, setPlanPagosActivo] = useState(false);
  const [cuotasPlan, setCuotasPlan] = useState([
    { fecha_vencimiento: '', monto: '' },
    { fecha_vencimiento: '', monto: '' }
  ]);

  // File upload
  const [archivoAdjunto, setArchivoAdjunto] = useState(null);
  const [archivoExistente, setArchivoExistente] = useState(null);

  // Detail view
  const [facturaDetalle, setFacturaDetalle] = useState(null);
  const [eliminandoFactura, setEliminandoFactura] = useState(null);

  // Load data
  const cargarFacturas = useCallback(async () => {
    try {
      const vars = { tipo: tipoVista };
      if (filtroEstado !== 'todos') vars.estado = filtroEstado;

      const data = await client.query(`
        query($estado: String, $tipo: String) {
          facturas(estado: $estado, tipo: $tipo) {
            facturas {
              id proveedor_id proyecto_id numero_factura fecha_emision fecha_vencimiento
              subtotal iva porcentaje_iva monto monto_pagado saldo_pendiente
              estado estado_calculado dias_vencido descripcion categoria orden_compra notas archivo_factura
              retencion_fuente retencion_iva otras_retenciones
              es_cuota_plan numero_cuota total_cuotas grupo_factura_id tipo es_posventa
              proveedor_nombre proveedor_tipo proyecto_nombre nombre_cliente apartamento
              capitulo_id capitulo_numero capitulo_nombre valor_asignado_proyecto
            }
            estadisticas {
              total_facturas monto_total monto_pagado_total saldo_pendiente_total
              facturas_pendientes facturas_pagadas facturas_parciales facturas_vencidas
            }
          }
        }
      `, vars);
      setFacturas(data.facturas.facturas || []);
      setEstadisticas(data.facturas.estadisticas || null);
    } catch (err) {
      console.error('Error cargando facturas:', err);
    } finally {
      setLoading(false);
    }
  }, [filtroEstado, tipoVista]);

  const cargarProveedores = async () => {
    try {
      const data = await client.query(`{ proveedores { proveedores { id nombre tipo clasificacion nit contacto_nombre porcentaje_retencion base_retencion retenciones { tipo_retencion_id tipo_retencion_codigo porcentaje_retencion base_retencion es_principal } } } }`);
      setProveedores(data.proveedores.proveedores || []);
    } catch (err) {
      console.error('Error cargando proveedores:', err);
    }
  };

  const cargarProyectos = async () => {
    try {
      const data = await client.query(`{ proyectos(incluir_especiales: true) { id proyecto_nombre nombre_cliente apartamento valor_final estado } }`);
      setProyectos(data.proyectos || []);
    } catch (err) {
      console.error('Error cargando proyectos:', err);
    }
  };

  const cargarConceptosBD = async () => {
    try {
      const data = await client.query(`{ conceptosFactura { id nombre activo conceptos { id nombre activo puede_repetir } } }`);
      setConceptosBD(data.conceptosFactura || []);
    } catch (err) {
      console.error('Error cargando conceptos:', err);
    }
  };

  const cargarPosventa = async () => {
    try {
      const data = await client.query(`{ posventaSolicitudes { id proyecto_nombre apartamento } }`);
      // Posventa es TODO lo que existe en el módulo de posventa (se crea manual, aparte de
      // los proyectos de obra). Se lista cada apartamento (nombre + # apto), deduplicado.
      // Al facturar, se resuelve/crea una entrada posventa separada — nunca un proyecto real.
      const norm = (s) => (s || '').toString().normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
      const porApto = new Map();
      for (const s of (data.posventaSolicitudes || [])) {
        const key = norm(s.proyecto_nombre) + '|' + norm(s.apartamento);
        if (porApto.has(key)) continue;
        porApto.set(key, { proyecto_nombre: (s.proyecto_nombre || '').trim(), apartamento: (s.apartamento || '').trim() });
      }
      setPosventaList(Array.from(porApto.values()).sort((a, b) => a.proyecto_nombre.localeCompare(b.proyecto_nombre)));
    } catch {}
  };

  const crearPosventaInline = async () => {
    if (!formPosventa.proyecto_nombre.trim() || !formPosventa.apartamento.trim()) {
      showError('Nombre y apartamento son obligatorios'); return;
    }
    try {
      const r = await client.mutate(
        `mutation($p: String!, $a: String!) { crearPosventa(proyecto_nombre: $p, apartamento: $a) { success message factura_id } }`,
        { p: formPosventa.proyecto_nombre, a: formPosventa.apartamento }
      );
      if (r.crearPosventa.success) {
        success('Apartamento posventa creado');
        setFormPosventa({ proyecto_nombre: '', apartamento: '' });
        setMostrarFormPosventa(false);
        await cargarPosventa();
      } else showError(r.crearPosventa.message);
    } catch { showError('Error al crear'); }
  };

  useEffect(() => {
    cargarConceptosBD();
    cargarFacturas();
    cargarProveedores();
    cargarProyectos();
    cargarPosventa();
  }, [cargarFacturas]);

  // Para escoger qué retención aplicar cuando el proveedor tiene varias
  const [retencionSeleccionadaIdFact, setRetencionSeleccionadaIdFact] = useState(null);

  // Inicializar la retención seleccionada cuando cambia el proveedor
  useEffect(() => {
    const rets = proveedorSeleccionado?.retenciones || [];
    if (rets.length === 0) { setRetencionSeleccionadaIdFact(null); return; }
    const principal = rets.find(r => r.es_principal == 1) || rets[0];
    setRetencionSeleccionadaIdFact(principal?.tipo_retencion_id ?? principal?.id ?? null);
  }, [proveedorSeleccionado?.id]);

  // Calculate retentions when proveedor or subtotal changes
  useEffect(() => {
    calcularRetenciones();
  }, [formData.subtotal, formData.porcentaje_iva, proveedorSeleccionado, retencionSeleccionadaIdFact]);

  const calcularRetenciones = () => {
    const subtotal = parseFloat(formData.subtotal) || 0;
    const porcentaje_iva = formData.porcentaje_iva !== '' ? parseFloat(formData.porcentaje_iva) : 0;
    const iva = subtotal * (porcentaje_iva / 100);
    const monto_total = subtotal + iva;

    // Sin fallback a campos legacy. Si no hay retenciones configuradas → 0.
    const rets = proveedorSeleccionado?.retenciones || [];
    const retencionSeleccionada = rets.find(r =>
      (r.tipo_retencion_id ?? r.id) === retencionSeleccionadaIdFact
    ) || rets.find(r => r.es_principal == 1) || rets[0] || null;

    const porcentaje_retencion = parseFloat(retencionSeleccionada?.porcentaje_retencion) || 0;
    const base_retencion = parseFloat(retencionSeleccionada?.base_retencion) || 0;
    const tipo_retencion_codigo = retencionSeleccionada?.tipo_retencion_codigo;
    const es_regimen_simple = tipo_retencion_codigo === 'IVA_REGIMEN_SIMPLE';

    let retencion_fuente = 0;
    let retencion_iva = 0;
    if (porcentaje_retencion > 0 && subtotal >= base_retencion) {
      if (es_regimen_simple) {
        retencion_iva = iva * (porcentaje_retencion / 100);
      } else {
        retencion_fuente = subtotal * (porcentaje_retencion / 100);
      }
    }

    const total_retenciones = retencion_fuente + retencion_iva;
    const valor_neto_pagar = monto_total - total_retenciones;

    setCalculos({ iva, monto_total, retencion_fuente, retencion_iva, total_retenciones, valor_neto_pagar });
  };

  // Handle proveedor selection
  // Descuentos con saldo del proveedor, para ofrecerlos al montar la factura.
  const [descDisponibles, setDescDisponibles] = useState([]);
  const [descSeleccionados, setDescSeleccionados] = useState([]);
  // Al editar, cuanto de cada descuento se aplica a ESTA factura { [id]: valor }.
  const [descMontos, setDescMontos] = useState({});
  // Lo ya pagado de la factura que se esta editando: la factura no puede quedar
  // por debajo de eso.
  const [pagadoEnEdicion, setPagadoEnEdicion] = useState(0);

  // Lo máximo que se le puede restar a la factura: su total menos lo ya pagado (al editar).
  const capacidadDescuento = Math.max(0, (parseFloat(formData.subtotal) || 0) - (facturaEditandoId ? pagadoEnEdicion : 0));
  const topeDescuento = (d) => Math.round(Math.min(Number(d?.saldo || 0), capacidadDescuento));
  const montoDescuento = (d) => {
    const tope = topeDescuento(d);
    const v = descMontos[d.id] !== undefined && descMontos[d.id] !== '' ? parseFloat(descMontos[d.id]) : tope;
    return Math.max(0, Math.min(isNaN(v) ? 0 : v, tope));
  };

  const cargarDescuentosProveedor = async (proveedorId) => {
    if (!proveedorId) { setDescDisponibles([]); setDescSeleccionados([]); setDescMontos({}); return; }
    try {
      const d = await client.query(
        `query($p: Int!) { descuentosDisponiblesProveedor(proveedor_id: $p) {
          id motivo saldo fecha proyecto_nombre apartamento capitulo_nombre } }`,
        { p: parseInt(proveedorId) });
      setDescDisponibles(d.descuentosDisponiblesProveedor || []);
    } catch { setDescDisponibles([]); }
    setDescSeleccionados([]);
    setDescMontos({});
  };

  const handleProveedorChange = async (proveedorId) => {
    setFormData(prev => ({ ...prev, proveedor_id: proveedorId }));
    cargarDescuentosProveedor(proveedorId);
    if (!proveedorId) {
      setProveedorSeleccionado(null);
      return;
    }
    try {
      const data = await client.query(`query($id: Int!) { proveedor(id: $id) { id nombre nit tipo porcentaje_retencion base_retencion retenciones { tipo_retencion_id tipo_retencion_codigo porcentaje_retencion base_retencion es_principal } tipo_retencion_codigo tipo_retencion_nombre regimen_tributario } }`, { id: parseInt(proveedorId) });
      if (data.proveedor) setProveedorSeleccionado(data.proveedor);
    } catch (err) {
      console.error('Error cargando proveedor:', err);
    }
  };

  // Validar número de factura duplicado por proveedor
  const validarFacturaDuplicada = useCallback((numFactura, proveedorId) => {
    if (!numFactura || !proveedorId) { setFacturaExiste(false); return; }
    const existe = facturas.some(f =>
      f.numero_factura === numFactura && String(f.proveedor_id) === String(proveedorId) && f.id !== facturaEditandoId
    );
    setFacturaExiste(existe);
  }, [facturas]);

  // Click outside para cerrar dropdown proveedor
  useEffect(() => {
    const handleClick = (e) => {
      if (proveedorDropdownRef.current && !proveedorDropdownRef.current.contains(e.target)) {
        setShowProveedorDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Load chapters for a project
  const cargarCapitulos = async (proyectoId) => {
    try {
      // Cargar capítulos del proyecto via GraphQL
      const data = await client.query(`query($id: Int!) { proyecto(id: $id) { capitulos { id numero capitulo_nombre subtotal valor_ejecutado_total } } }`, { id: parseInt(proyectoId) });
      return data.proyecto?.capitulos || [];
    } catch (err) {
      console.error('Error cargando capítulos:', err);
      return [];
    }
  };

  // Project distribution
  const agregarProyecto = async (proyecto) => {
    if (proyectosDistribucion.find(p => p.proyecto_id === proyecto.id)) return;
    // Posventa no maneja capitulos: no se cargan ni se exigen.
    const capitulos = proyecto.es_posventa ? [] : await cargarCapitulos(proyecto.id);
    setProyectosDistribucion(prev => [...prev, {
      proyecto_id: proyecto.id,
      nombre: `${proyecto.proyecto_nombre}${proyecto.apartamento ? ' - Apto ' + proyecto.apartamento : ''} - ${proyecto.nombre_cliente}`,
      valor: '',
      plan_pagos: false,
      cuotas: [],
      es_posventa: !!proyecto.es_posventa,
      capitulos: capitulos,
      capitulos_seleccionados: [],
      capitulo_conceptos: {}, // { [capitulo_id]: 'nombre del concepto' }
    }]);
    setBusquedaProyecto('');
    setMostrarListaProyectos(false);
  };

  const toggleCapitulo = (proyectoId, capituloId) => {
    setProyectosDistribucion(prev => prev.map(p => {
      if (p.proyecto_id !== proyectoId) return p;
      const seleccionados = p.capitulos_seleccionados || [];
      const existe = seleccionados.includes(capituloId);
      const nuevosSeleccionados = existe
        ? seleccionados.filter(id => id !== capituloId)
        : [...seleccionados, capituloId];
      // Si se desmarca, eliminar el concepto asociado
      const nuevosConceptos = { ...(p.capitulo_conceptos || {}) };
      if (existe) delete nuevosConceptos[capituloId];
      return {
        ...p,
        capitulos_seleccionados: nuevosSeleccionados,
        capitulo_conceptos: nuevosConceptos,
      };
    }));
  };

  const setConceptoCapitulo = (proyectoId, capituloId, concepto) => {
    setProyectosDistribucion(prev => prev.map(p => {
      if (p.proyecto_id !== proyectoId) return p;
      return {
        ...p,
        capitulo_conceptos: { ...(p.capitulo_conceptos || {}), [capituloId]: concepto },
      };
    }));
  };

  const verificarConcepto = async (proyectoId, capituloId, conceptoNombre) => {
    if (!conceptoNombre || !proyectoId || !capituloId) {
      setConceptoViolaciones(prev => { const n = {...prev}; delete n[`${proyectoId}_${capituloId}`]; return n; });
      return;
    }
    // Check if concept has puede_repetir = false in local state
    const esUnico = conceptosBD.some(cap => cap.conceptos.some(c => c.nombre === conceptoNombre && c.puede_repetir === false));
    if (!esUnico) {
      setConceptoViolaciones(prev => { const n = {...prev}; delete n[`${proyectoId}_${capituloId}`]; return n; });
      return;
    }
    try {
      const data = await client.query(`query($pid: Int!, $cid: Int!, $cn: String!) { verificarConceptoUso(proyecto_id: $pid, capitulo_id: $cid, concepto_nombre: $cn) }`, { pid: proyectoId, cid: capituloId, cn: conceptoNombre });
      setConceptoViolaciones(prev => ({ ...prev, [`${proyectoId}_${capituloId}`]: data.verificarConceptoUso }));
    } catch { /* ignore */ }
  };

  const quitarProyecto = (proyectoId) => {
    setProyectosDistribucion(prev => prev.filter(p => p.proyecto_id !== proyectoId));
  };

  const actualizarValorProyecto = (proyectoId, valor) => {
    setProyectosDistribucion(prev => prev.map(p =>
      p.proyecto_id === proyectoId ? { ...p, valor } : p
    ));
  };

  // Auto-distribute evenly
  const distribuirEquitativamente = () => {
    const subtotal = parseFloat(formData.subtotal) || 0;
    if (!subtotal || !proyectosDistribucion.length) return;
    const valorPorProyecto = Math.round(subtotal / proyectosDistribucion.length);
    setProyectosDistribucion(prev => prev.map((p, i) => ({
      ...p,
      valor: i === prev.length - 1
        ? (subtotal - valorPorProyecto * (prev.length - 1)).toString()
        : valorPorProyecto.toString()
    })));
  };

  // Payment plan per project
  const togglePlanPagos = (proyectoId) => {
    setProyectosDistribucion(prev => prev.map(p => {
      if (p.proyecto_id !== proyectoId) return p;
      const activar = !p.plan_pagos;
      return {
        ...p,
        plan_pagos: activar,
        cuotas: activar ? [{ fecha_vencimiento: '', monto: '', concepto: '' }, { fecha_vencimiento: '', monto: '', concepto: '' }] : []
      };
    }));
  };

  const actualizarCuota = (proyectoId, index, field, value) => {
    setProyectosDistribucion(prev => prev.map(p => {
      if (p.proyecto_id !== proyectoId) return p;
      const cuotas = [...p.cuotas];
      cuotas[index] = { ...cuotas[index], [field]: value };
      return { ...p, cuotas };
    }));
  };

  const agregarCuota = (proyectoId) => {
    setProyectosDistribucion(prev => prev.map(p => {
      if (p.proyecto_id !== proyectoId) return p;
      return { ...p, cuotas: [...p.cuotas, { fecha_vencimiento: '', monto: '', concepto: '' }] };
    }));
  };

  const quitarCuota = (proyectoId, index) => {
    setProyectosDistribucion(prev => prev.map(p => {
      if (p.proyecto_id !== proyectoId) return p;
      return { ...p, cuotas: p.cuotas.filter((_, i) => i !== index) };
    }));
  };

  // Submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Si estamos editando, usar handleUpdate
    if (facturaEditandoId) {
      handleUpdate();
      return;
    }

    if (!formData.proveedor_id) { showError('Seleccione un proveedor'); return; }
    if (!formData.numero_factura) { showError('Ingrese el numero de factura'); return; }
    if (facturaExiste) { showError('Este número de factura ya existe para este proveedor'); return; }
    if (!formData.subtotal || parseFloat(formData.subtotal) <= 0) { showError('Ingrese el valor'); return; }

    // Validaciones específicas por tipo
    if (tipoVista === 'administrativa') {
      if (!formData.fecha_vencimiento) { showError('Ingrese la fecha de vencimiento'); return; }
    } else {
      if (!proyectosDistribucion.length) { showError('Seleccione al menos un proyecto'); return; }
    }
    const tienePlanPagos = tipoVista !== 'administrativa' && proyectosDistribucion.some(p => p.plan_pagos && p.cuotas?.length > 0);
    if (!tienePlanPagos && !formData.fecha_vencimiento) { showError('Ingrese la fecha de vencimiento'); return; }

    const subtotal = parseFloat(formData.subtotal) || 0;

    // Validate distribution (solo para facturas de proyecto)
    if (tipoVista !== 'administrativa') {
      const totalDist = proyectosDistribucion.reduce((sum, p) => sum + (parseFloat(p.valor) || 0), 0);
      if (Math.abs(totalDist - subtotal) > 1) {
        showError(`La suma de distribuciones ($${formatMoney(totalDist)}) debe ser igual al subtotal ($${formatMoney(subtotal)})`);
        return;
      }

      // Cada proyecto debe tener al menos un capítulo seleccionado para que
      // el "Ejecutado (Facturas)" del Resumen sume correctamente.
      // Excepción: las facturas de POSVENTA no exigen capítulo.
      const sinCapitulos = proyectosDistribucion.find(p => !p.es_posventa && !(p.capitulos_seleccionados?.length > 0));
      if (sinCapitulos) {
        showError(`Seleccione al menos un capítulo para el proyecto "${sinCapitulos.nombre}"`);
        return;
      }
    }

    // Validate plan de pagos cuotas (solo para facturas de proyecto)
    if (tienePlanPagos) {
      for (const pd of proyectosDistribucion.filter(p => p.plan_pagos && p.cuotas?.length > 0)) {
        const cuotasValidas = pd.cuotas.filter(c => c.monto && c.fecha_vencimiento);
        if (cuotasValidas.length === 0) {
          showError(`Agregue al menos una cuota con fecha y monto al plan de pagos del proyecto "${pd.nombre}"`);
          return;
        }
        const totalCuotas = cuotasValidas.reduce((s, c) => s + (parseFloat(c.monto) || 0), 0);
        const valorProyecto = parseFloat(pd.valor) || 0;
        if (Math.abs(totalCuotas - valorProyecto) > 1) {
          showError(`La suma de cuotas ($${formatMoney(totalCuotas)}) del proyecto "${pd.nombre}" debe ser igual a su valor ($${formatMoney(valorProyecto)})`);
          return;
        }
      }
    }

    const tieneViolacion = Object.values(conceptoViolaciones).some(Boolean);
    if (tieneViolacion) {
      showError('Hay conceptos únicos que ya fueron usados en ese capítulo y proyecto. Revisa los conceptos marcados en rojo.');
      return;
    }

    setGuardando(true);

    try {
      let resultData;

      if (tipoVista === 'administrativa') {
        // Factura administrativa: sin proyecto, sin concepto
        const gqlData = await client.mutate(`
          mutation($input: CrearFacturaAdminInput!) {
            crearFacturaAdministrativa(input: $input) { success message factura_id }
          }
        `, { input: {
          proveedor_id: parseInt(formData.proveedor_id),
          numero_factura: formData.numero_factura,
          fecha_emision: formData.fecha_emision,
          fecha_vencimiento: formData.fecha_vencimiento || null,
          subtotal: subtotal,
          iva: 0, porcentaje_iva: 0,
          descripcion: formData.descripcion || null,
          notas: formData.notas || null,
          retencion_fuente: calculos.retencion_fuente || null,
          retencion_iva: calculos.retencion_iva || null,
          otras_retenciones: null,
        }});
        resultData = gqlData.crearFacturaAdministrativa;
      } else {
        // Factura de proyecto: flujo normal
        // Derivar categoría: primer concepto encontrado entre los capítulos seleccionados de cualquier proyecto
        let categoriaDerivada = formData.categoria || null;
        if (!categoriaDerivada) {
          for (const p of proyectosDistribucion) {
            const conceptos = Object.values(p.capitulo_conceptos || {}).filter(Boolean);
            if (conceptos.length > 0) { categoriaDerivada = conceptos[0]; break; }
          }
        }
        const gqlData = await client.mutate(`
          mutation($input: CrearFacturaInput!) {
            crearFactura(input: $input) { success message factura_id }
          }
        `, { input: {
          proveedor_id: parseInt(formData.proveedor_id),
          numero_factura: formData.numero_factura,
          fecha_emision: formData.fecha_emision,
          fecha_vencimiento: formData.fecha_vencimiento || null,
          subtotal: subtotal,
          iva: 0, porcentaje_iva: 0,
          descripcion: formData.descripcion || null,
          categoria: categoriaDerivada,
          retencion_fuente: calculos.retencion_fuente || null,
          retencion_iva: calculos.retencion_iva || null,
          otras_retenciones: null,
          proyectos: proyectosDistribucion.map(p => ({
            proyecto_id: p.proyecto_id,
            valor_asignado: parseFloat(p.valor) || 0,
            capitulos: p.capitulos_seleccionados || [],
            es_posventa: !!p.es_posventa,
            concepto: Object.values(p.capitulo_conceptos || {}).filter(Boolean)[0] || null,
            cuotas: p.plan_pagos && p.cuotas?.length > 0
              ? p.cuotas.filter(c => c.monto && c.fecha_vencimiento).map(c => ({
                  monto: parseFloat(c.monto) || 0,
                  fecha_vencimiento: c.fecha_vencimiento,
                  concepto: c.concepto || null,
                }))
              : null,
          })),
        }});
        resultData = gqlData.crearFactura;
      }

      if (resultData.success) {
        // Upload file if exists
        if (archivoAdjunto && resultData.factura_id) {
          const formDataFile = new FormData();
          formDataFile.append('factura_id', resultData.factura_id);
          formDataFile.append('archivo', archivoAdjunto);
          const token = localStorage.getItem('tuvi_token');
          await fetch(`${(await import('../../config')).UPLOAD_URL}/factura`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` },
            body: formDataFile
          });
        }

        // Descuentos del proveedor elegidos en el formulario, con el monto que el
        // usuario escribió: se aplican sobre la factura recien creada, bajando su
        // monto y consumiendo el saldo; lo que no se use queda para la siguiente.
        const aplicacionesNuevas = descSeleccionados
          .map(id => ({ descuento_id: id, valor: montoDescuento(descDisponibles.find(x => x.id === id)) }))
          .filter(a => a.valor > 0);
        if (aplicacionesNuevas.length > 0 && resultData.factura_id) {
          try {
            const ap = await client.mutate(
              `mutation($fid: Int!, $aps: [AplicacionDescuentoInput!]) { aplicarDescuentosAFactura(factura_id: $fid, aplicaciones: $aps) { success message } }`,
              { fid: resultData.factura_id, aps: aplicacionesNuevas });
            if (ap.aplicarDescuentosAFactura.success) success(ap.aplicarDescuentosAFactura.message);
            else showError(ap.aplicarDescuentosAFactura.message || 'La factura se creó pero el descuento no se pudo aplicar');
          } catch {
            showError('La factura se creó, pero el descuento no se pudo aplicar');
          }
        }

        success('Factura creada exitosamente');
        resetFormulario();
        setMostrarFormulario(false);
        cargarFacturas();
      } else {
        showError(resultData.message || 'Error al crear factura');
      }
    } catch (err) {
      console.error('Error:', err);
      showError('Error al crear la factura');
    } finally {
      setGuardando(false);
    }
  };

  const resetFormulario = () => {
    setFacturaEditandoId(null);
    setBusquedaProveedor('');
    setFacturaExiste(false);
    setDescDisponibles([]);
    setDescSeleccionados([]);
    setDescMontos({});
    setPagadoEnEdicion(0);
    setFormData({
      numero_factura: '', fecha_emision: new Date().toISOString().split('T')[0],
      fecha_vencimiento: '', descripcion: '', categoria: '', proveedor_id: '', subtotal: '', porcentaje_iva: 0
    });
    setProyectosDistribucion([]);
    setProveedorSeleccionado(null);
    setPlanPagosActivo(false);
    setCuotasPlan([{ fecha_vencimiento: '', monto: '' }, { fecha_vencimiento: '', monto: '' }]);
    setArchivoAdjunto(null);
    setArchivoExistente(null);
    setConceptoViolaciones({});
  };

  // Facturas filtradas por vencimiento
  const facturasVencFiltradas = facturas.filter(f => {
    if (!filtroVencDesde && !filtroVencHasta) return false;
    const fv = f.fecha_vencimiento;
    if (!fv) return false;
    if (filtroVencDesde && fv < filtroVencDesde) return false;
    if (filtroVencHasta && fv > filtroVencHasta) return false;
    return true;
  });

  // Facturas filtradas por proveedor + rango
  const facturasProvFiltradas = facturas.filter(f => {
    if (!filtroProvId || !filtroProvFechaDesde || !filtroProvFechaHasta) return false;
    if (String(f.proveedor_id) !== String(filtroProvId)) return false;
    const fe = f.fecha_emision;
    if (!fe) return false;
    if (fe < filtroProvFechaDesde || fe > filtroProvFechaHasta) return false;
    return true;
  });

  // Lista única de proyectos derivada de las facturas (id, nombre, apartamento, cliente)
  const proyectosUnicos = useMemo(() => {
    const map = new Map();
    facturas.forEach(f => {
      if (f.proyecto_id && !map.has(f.proyecto_id)) {
        map.set(f.proyecto_id, {
          id: f.proyecto_id,
          nombre: f.proyecto_nombre || '',
          apartamento: f.apartamento || '',
          nombre_cliente: f.nombre_cliente || '',
        });
      }
    });
    return [...map.values()].sort((a, b) => (a.nombre || '').localeCompare(b.nombre || ''));
  }, [facturas]);

  // Facturas filtradas por proyecto + rango
  const facturasProyFiltradas = facturas.filter(f => {
    if (!filtroProyId || !filtroProyFechaDesde || !filtroProyFechaHasta) return false;
    if (String(f.proyecto_id) !== String(filtroProyId)) return false;
    const fe = f.fecha_emision;
    if (!fe) return false;
    if (fe < filtroProyFechaDesde || fe > filtroProyFechaHasta) return false;
    return true;
  });

  // Excel helpers
  const crearExcelProfesional = (titulo, columnas, filas, nombreArchivo) => {
    const aoa = [];
    // Titulo
    aoa.push([titulo]);
    aoa.push([`Generado: ${new Date().toLocaleDateString('es-CO', { day: '2-digit', month: 'long', year: 'numeric' })}`]);
    aoa.push([]);
    // Headers
    aoa.push(columnas.map(c => c.header));
    // Data
    filas.forEach(fila => aoa.push(columnas.map(c => c.value(fila))));
    // Totales
    const totalIdx = columnas.findIndex(c => c.key === 'monto');
    const saldoIdx = columnas.findIndex(c => c.key === 'saldo');
    if (totalIdx >= 0 || saldoIdx >= 0) {
      const totalRow = columnas.map(() => '');
      totalRow[0] = 'TOTALES';
      if (totalIdx >= 0) totalRow[totalIdx] = filas.reduce((s, f) => s + (columnas[totalIdx].rawValue?.(f) || 0), 0);
      if (saldoIdx >= 0) totalRow[saldoIdx] = filas.reduce((s, f) => s + (columnas[saldoIdx].rawValue?.(f) || 0), 0);
      aoa.push([]);
      aoa.push(totalRow);
    }

    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!cols'] = columnas.map(c => ({ wch: c.width || 15 }));
    // Merge titulo
    ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: columnas.length - 1 } }];
    // Estilos basicos para header
    for (let i = 0; i < columnas.length; i++) {
      const cellRef = XLSX.utils.encode_cell({ r: 3, c: i });
      if (!ws[cellRef]) ws[cellRef] = { v: columnas[i].header, t: 's' };
      ws[cellRef].s = { font: { bold: true, color: { rgb: 'FFFFFF' } }, fill: { fgColor: { rgb: '1C2837' } }, alignment: { horizontal: 'center' }, border: { bottom: { style: 'thin', color: { rgb: '000000' } } } };
    }
    // Estilo titulo
    const titleCell = XLSX.utils.encode_cell({ r: 0, c: 0 });
    if (ws[titleCell]) ws[titleCell].s = { font: { bold: true, sz: 14, color: { rgb: '1C2837' } } };
    // Formato moneda en columnas de monto
    for (let r = 4; r < aoa.length; r++) {
      columnas.forEach((col, ci) => {
        if (col.key === 'monto' || col.key === 'saldo' || col.key === 'retenciones' || col.key === 'neto') {
          const ref = XLSX.utils.encode_cell({ r, c: ci });
          if (ws[ref] && typeof ws[ref].v === 'number') ws[ref].z = '#,##0';
        }
      });
    }

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Reporte');
    XLSX.writeFile(wb, nombreArchivo);
    success('Excel descargado');
  };

  const exportarVencimiento = () => {
    if (facturasVencFiltradas.length === 0) { showError('No hay facturas en ese rango'); return; }
    const cols = [
      { header: '# Factura', key: 'num', width: 12, value: f => f.numero_factura },
      { header: 'Proveedor', key: 'prov', width: 28, value: f => f.proveedor_nombre || '-' },
      { header: 'Proyecto', key: 'proy', width: 22, value: f => f.proyecto_nombre || '-' },
      { header: 'Apto', key: 'apto', width: 8, value: f => f.apartamento || '-' },
      { header: 'Descripcion', key: 'desc', width: 30, value: f => f.descripcion || '' },
      { header: 'F. Emision', key: 'emision', width: 14, value: f => f.fecha_emision || '-' },
      { header: 'F. Vencimiento', key: 'venc', width: 14, value: f => f.fecha_vencimiento || '-' },
      { header: 'Monto', key: 'monto', width: 16, value: f => parseFloat(f.monto) || 0, rawValue: f => parseFloat(f.monto) || 0 },
      { header: 'Saldo', key: 'saldo', width: 16, value: f => parseFloat(f.saldo_pendiente) || 0, rawValue: f => parseFloat(f.saldo_pendiente) || 0 },
      { header: 'Estado', key: 'estado', width: 12, value: f => (f.estado || '').toUpperCase() },
    ];
    crearExcelProfesional(
      `Facturas por Vencimiento: ${filtroVencDesde} a ${filtroVencHasta}`,
      cols, facturasVencFiltradas,
      `Facturas_Vencimiento_${filtroVencDesde}_${filtroVencHasta}.xlsx`
    );
  };

  const exportarProyecto = () => {
    if (facturasProyFiltradas.length === 0) { showError('No hay facturas para ese proyecto en ese rango'); return; }
    const proy = proyectosUnicos.find(p => String(p.id) === String(filtroProyId));
    const proyEtiqueta = proy
      ? `${proy.nombre}${proy.apartamento ? ' · Apto ' + proy.apartamento : ''}`
      : 'Proyecto';
    const cols = [
      { header: '# Factura', key: 'num', width: 12, value: f => f.numero_factura },
      { header: 'Proveedor', key: 'prov', width: 28, value: f => f.proveedor_nombre || '-' },
      { header: 'Proyecto', key: 'proy', width: 22, value: f => f.proyecto_nombre || '-' },
      { header: 'Apto', key: 'apto', width: 10, value: f => f.apartamento || '-' },
      { header: 'Cliente', key: 'cliente', width: 24, value: f => f.nombre_cliente || '-' },
      { header: 'Descripcion', key: 'desc', width: 30, value: f => f.descripcion || '' },
      { header: 'F. Emision', key: 'emision', width: 14, value: f => f.fecha_emision || '-' },
      { header: 'F. Vencimiento', key: 'venc', width: 14, value: f => f.fecha_vencimiento || '-' },
      { header: 'Monto', key: 'monto', width: 16, value: f => parseFloat(f.monto) || 0, rawValue: f => parseFloat(f.monto) || 0 },
      { header: 'Retenciones', key: 'retenciones', width: 14, value: f => (parseFloat(f.retencion_fuente)||0) + (parseFloat(f.retencion_iva)||0) + (parseFloat(f.otras_retenciones)||0), rawValue: f => (parseFloat(f.retencion_fuente)||0) + (parseFloat(f.retencion_iva)||0) + (parseFloat(f.otras_retenciones)||0) },
      { header: 'Neto', key: 'neto', width: 16, value: f => (parseFloat(f.monto)||0) - ((parseFloat(f.retencion_fuente)||0) + (parseFloat(f.retencion_iva)||0) + (parseFloat(f.otras_retenciones)||0)), rawValue: f => (parseFloat(f.monto)||0) - ((parseFloat(f.retencion_fuente)||0) + (parseFloat(f.retencion_iva)||0) + (parseFloat(f.otras_retenciones)||0)) },
      { header: 'Saldo', key: 'saldo', width: 16, value: f => parseFloat(f.saldo_pendiente) || 0, rawValue: f => parseFloat(f.saldo_pendiente) || 0 },
      { header: 'Estado', key: 'estado', width: 12, value: f => (f.estado || '').toUpperCase() },
    ];
    const proyArchivo = (proy?.nombre || 'Proyecto') + (proy?.apartamento ? '_Apto' + proy.apartamento : '');
    crearExcelProfesional(
      `Facturas de ${proyEtiqueta}: ${filtroProyFechaDesde} a ${filtroProyFechaHasta}`,
      cols, facturasProyFiltradas,
      `Facturas_${proyArchivo.replace(/\s+/g, '_')}_${filtroProyFechaDesde}_${filtroProyFechaHasta}.xlsx`
    );
  };

  const exportarProveedor = () => {
    if (facturasProvFiltradas.length === 0) { showError('No hay facturas para ese proveedor en ese rango'); return; }
    const provNombre = proveedores.find(p => String(p.id) === String(filtroProvId))?.nombre || 'Proveedor';
    const cols = [
      { header: '# Factura', key: 'num', width: 12, value: f => f.numero_factura },
      { header: 'Proyecto', key: 'proy', width: 22, value: f => f.proyecto_nombre || '-' },
      { header: 'Apto', key: 'apto', width: 8, value: f => f.apartamento || '-' },
      { header: 'Descripcion', key: 'desc', width: 30, value: f => f.descripcion || '' },
      { header: 'F. Emision', key: 'emision', width: 14, value: f => f.fecha_emision || '-' },
      { header: 'F. Vencimiento', key: 'venc', width: 14, value: f => f.fecha_vencimiento || '-' },
      { header: 'Monto', key: 'monto', width: 16, value: f => parseFloat(f.monto) || 0, rawValue: f => parseFloat(f.monto) || 0 },
      { header: 'Retenciones', key: 'retenciones', width: 14, value: f => (parseFloat(f.retencion_fuente)||0) + (parseFloat(f.retencion_iva)||0) + (parseFloat(f.otras_retenciones)||0), rawValue: f => (parseFloat(f.retencion_fuente)||0) + (parseFloat(f.retencion_iva)||0) + (parseFloat(f.otras_retenciones)||0) },
      { header: 'Neto', key: 'neto', width: 16, value: f => (parseFloat(f.monto)||0) - ((parseFloat(f.retencion_fuente)||0) + (parseFloat(f.retencion_iva)||0) + (parseFloat(f.otras_retenciones)||0)), rawValue: f => (parseFloat(f.monto)||0) - ((parseFloat(f.retencion_fuente)||0) + (parseFloat(f.retencion_iva)||0) + (parseFloat(f.otras_retenciones)||0)) },
      { header: 'Saldo', key: 'saldo', width: 16, value: f => parseFloat(f.saldo_pendiente) || 0, rawValue: f => parseFloat(f.saldo_pendiente) || 0 },
      { header: 'Estado', key: 'estado', width: 12, value: f => (f.estado || '').toUpperCase() },
    ];
    crearExcelProfesional(
      `Facturas de ${provNombre}: ${filtroProvFechaDesde} a ${filtroProvFechaHasta}`,
      cols, facturasProvFiltradas,
      `Facturas_${provNombre.replace(/\s+/g, '_')}_${filtroProvFechaDesde}_${filtroProvFechaHasta}.xlsx`
    );
  };

  // Delete invoice
  // Abrir edición de factura
  const abrirEdicion = (factura) => {
    setFacturaEditandoId(factura.id);
    setFormData({
      numero_factura: factura.numero_factura || '',
      fecha_emision: factura.fecha_emision || '',
      fecha_vencimiento: factura.fecha_vencimiento || '',
      descripcion: factura.descripcion || '',
      categoria: factura.categoria || '',
      proveedor_id: factura.proveedor_id || '',
      subtotal: factura.monto || factura.subtotal || '',
      porcentaje_iva: 0
    });
    setBusquedaProveedor(factura.proveedor_nombre || '');
    setArchivoExistente(factura.archivo_factura || null);
    setArchivoAdjunto(null);

    // Provisional mientras llegan las asignaciones reales, para que el modal no
    // aparezca vacio.
    if (factura.proyecto_nombre) {
      setProyectosDistribucion([{
        proyecto_id: factura.proyecto_id,
        nombre: factura.proyecto_nombre,
        valor: (factura.monto || factura.subtotal || '').toString(),
        capitulos: [],
        capitulos_seleccionados: [],
        capitulo_conceptos: {},
      }]);
    }
    setPagadoEnEdicion(parseFloat(factura.monto_pagado) || 0);
    handleProveedorChange(factura.proveedor_id);
    setMostrarFormulario(true);

    // Se traen los proyectos y CAPITULOS con los que quedo montada la factura,
    // para poder verlos y cambiarlos al editar. Antes no se cargaban y el
    // capitulo asignado quedaba invisible.
    cargarAsignacionesFactura(factura.id);
  };

  // Arma proyectosDistribucion a partir de lo que la factura tiene guardado.
  // Una misma factura puede estar repartida en varios proyectos, y en cada uno
  // en uno o varios capitulos: por eso se agrupa por proyecto.
  const cargarAsignacionesFactura = async (facturaId) => {
    try {
      const d = await client.query(`query($id: Int!) { factura(id: $id) {
        proyectos_relacionados { proyecto_id proyecto_nombre nombre_cliente capitulo_id capitulo_nombre valor_asignado }
      } }`, { id: parseInt(facturaId) });
      const rels = d.factura?.proyectos_relacionados || [];
      if (rels.length === 0) return;

      const porProyecto = new Map();
      for (const r of rels) {
        if (!porProyecto.has(r.proyecto_id)) {
          porProyecto.set(r.proyecto_id, {
            proyecto_id: r.proyecto_id,
            nombre: `${r.proyecto_nombre || ''}${r.nombre_cliente ? ' - ' + r.nombre_cliente : ''}`.trim(),
            valor: 0,
            plan_pagos: false,
            cuotas: [],
            capitulos: [],
            capitulos_seleccionados: [],
            capitulo_conceptos: {},
          });
        }
        const p = porProyecto.get(r.proyecto_id);
        p.valor += Number(r.valor_asignado || 0);
        if (r.capitulo_id && !p.capitulos_seleccionados.includes(r.capitulo_id)) {
          p.capitulos_seleccionados.push(r.capitulo_id);
        }
      }

      const lista = [...porProyecto.values()];
      for (const p of lista) {
        p.capitulos = await cargarCapitulos(p.proyecto_id);
        p.valor = String(Math.round(p.valor));
      }
      setProyectosDistribucion(lista);
    } catch (err) {
      console.error('Error cargando las asignaciones de la factura:', err);
    }
  };

  // Actualizar factura existente
  const handleUpdate = async () => {
    if (!facturaEditandoId) return;

    // Al abrir la edicion ya vienen cargados los capitulos con los que la
    // factura quedo montada, asi que lo normal es mandar 'proyectos' y guardar
    // lo que quede seleccionado (el usuario puede cambiar de capitulo).
    // Si un proyecto queda SIN ningun capitulo marcado no se manda: se preserva
    // la asignacion que ya tenia y el backend re-escala valor_asignado al nuevo
    // monto. Eso cubre las facturas viejas que nunca tuvieron capitulo.
    const proyectosConCapitulos = proyectosDistribucion
      .filter(p => p.proyecto_id && (p.capitulos_seleccionados?.length > 0));

    setGuardando(true);
    try {
      const nuevoMonto = parseFloat(formData.subtotal) || 0;
      const proyectosPayload = proyectosConCapitulos.map(p => ({
        proyecto_id: parseInt(p.proyecto_id),
        // Usar el monto actualizado, no el valor viejo del state.
        // Si hay un solo proyecto, todo el monto va a él.
        // Si hay varios, dividir proporcionalmente segun el valor original.
        valor_asignado: proyectosConCapitulos.length === 1
          ? nuevoMonto
          : (parseFloat(p.valor) || 0),
        capitulos: (p.capitulos_seleccionados || []).map(c => parseInt(c)),
      }));

      const gqlData = await client.mutate(`
        mutation($id: Int!, $input: EditarFacturaInput!) {
          editarFactura(id: $id, input: $input) { success message }
        }
      `, { id: facturaEditandoId, input: {
        proveedor_id: formData.proveedor_id ? parseInt(formData.proveedor_id) : undefined,
        numero_factura: formData.numero_factura,
        fecha_emision: formData.fecha_emision,
        fecha_vencimiento: formData.fecha_vencimiento || null,
        descripcion: formData.descripcion,
        categoria: formData.categoria || null,
        monto: nuevoMonto,
        proyectos: proyectosPayload.length > 0 ? proyectosPayload : undefined,
      }});
      if (gqlData.editarFactura.success) {
        // Upload adjunto if new file selected (todavía PHP)
        if (archivoAdjunto) {
          const formDataFile = new FormData();
          formDataFile.append('factura_id', facturaEditandoId);
          formDataFile.append('archivo', archivoAdjunto);
          const token = localStorage.getItem('tuvi_token');
          await fetch(`${(await import('../../config')).UPLOAD_URL}/factura`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` },
            body: formDataFile
          });
        }
        // Descuentos marcados con su monto: se aplican DESPUES de guardar el
        // monto nuevo, para que el tope se calcule sobre el valor actualizado.
        if (descSeleccionados.length > 0) {
          const aplicaciones = descSeleccionados
            .map(id => ({ descuento_id: id, valor: montoDescuento(descDisponibles.find(x => x.id === id)) }))
            .filter(a => a.valor > 0);

          if (aplicaciones.length > 0) {
            try {
              const ap = await client.mutate(
                `mutation($fid: Int!, $aps: [AplicacionDescuentoInput!]) { aplicarDescuentosAFactura(factura_id: $fid, aplicaciones: $aps) { success message } }`,
                { fid: facturaEditandoId, aps: aplicaciones });
              if (ap.aplicarDescuentosAFactura.success) success(ap.aplicarDescuentosAFactura.message);
              else showError(ap.aplicarDescuentosAFactura.message || 'No se pudo aplicar el descuento');
            } catch {
              showError('La factura se guardó, pero el descuento no se pudo aplicar');
            }
          }
        }

        success('Factura actualizada');
        setMostrarFormulario(false);
        setFacturaEditandoId(null);
        resetFormulario();
        cargarFacturas();
      } else {
        showError(gqlData.editarFactura.message);
      }
    } catch (err) {
      showError('Error al actualizar factura');
    } finally {
      setGuardando(false);
    }
  };

  const handleEliminar = async (factura) => {
    if (!window.confirm(`Eliminar factura #${factura.numero_factura}?`)) return;
    setEliminandoFactura(factura.id);
    try {
      const gqlData = await client.mutate(`mutation($id: Int!) { eliminarFactura(id: $id) { success message } }`, { id: factura.id });
      if (gqlData.eliminarFactura.success) {
        success(gqlData.eliminarFactura.message);
        cargarFacturas();
      } else {
        showError(gqlData.eliminarFactura.message);
      }
    } catch (err) {
      showError('Error al eliminar');
    } finally {
      setEliminandoFactura(null);
    }
  };

  // Marcar / quitar una factura como posventa (garantía). Al marcarla sale del
  // presupuesto de obra; al quitarla vuelve a "Sin clasificar".
  const togglePosventa = async (factura) => {
    const nuevo = !factura.es_posventa;
    const msg = nuevo
      ? `¿Marcar la factura #${factura.numero_factura} como POSVENTA? Saldrá del presupuesto de obra.`
      : `¿Quitar posventa a la factura #${factura.numero_factura}? Volverá a contar como obra.`;
    if (!window.confirm(msg)) return;
    try {
      const r = await client.mutate(
        `mutation($id: Int!, $input: EditarFacturaInput!) { editarFactura(id: $id, input: $input) { success message } }`,
        { id: factura.id, input: { es_posventa: nuevo } }
      );
      if (r.editarFactura.success) { success(r.editarFactura.message); cargarFacturas(); }
      else showError(r.editarFactura.message);
    } catch (err) { showError('Error al cambiar posventa'); }
  };

  const formatMoney = (valor) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency', currency: 'COP', minimumFractionDigits: 0
    }).format(valor || 0);
  };

  const formatDate = (fecha) => {
    if (!fecha) return '-';
    const d = fecha.length === 10 ? new Date(fecha + 'T12:00:00-05:00') : new Date(fecha);
    return d.toLocaleDateString('es-CO', { timeZone: 'America/Bogota', year: 'numeric', month: 'short', day: 'numeric' });
  };

  // Conceptos ya usados por proyecto (para validación)
  const conceptosUsadosPorProyecto = (proyectoId) => {
    return facturas
      .filter(f => f.proyecto_id === proyectoId && f.categoria && f.id !== facturaEditandoId)
      .map(f => f.categoria);
  };

  const conceptoDisponible = (concepto, proyectoId) => {
    if (!concepto || !proyectoId) return true;
    const conf = CONCEPTOS_FACTURA.find(c => c.value === concepto);
    if (!conf || conf.repetible) return true;
    return !conceptosUsadosPorProyecto(proyectoId).includes(concepto);
  };

  // Filtered invoices
  const facturasFiltradas = facturas.filter(f =>
    matchesAnyField(f, ['numero_factura', 'proveedor_nombre', 'proyecto_nombre', 'apartamento', 'descripcion', 'nombre_cliente'], busqueda)
  );

  // Agrupar facturas que pertenecen a la misma factura original (multi-proyecto).
  // Las facturas con `grupo_factura_id` se unen en un solo card. Las demás quedan sueltas.
  // El orden original de la lista se preserva usando el índice de la primera factura del grupo.
  const facturasAgrupadas = (() => {
    const grupos = new Map();
    const orden = [];
    facturasFiltradas.forEach((f, idx) => {
      const key = f.grupo_factura_id ? `g_${f.grupo_factura_id}` : `f_${f.id}`;
      if (!grupos.has(key)) {
        grupos.set(key, { key, esGrupo: !!f.grupo_factura_id, facturas: [], firstIdx: idx });
        orden.push(key);
      }
      grupos.get(key).facturas.push(f);
    });
    return orden.map(k => grupos.get(k));
  })();

  // Los proyectos especiales tienen su propia pestaña, así que salen de esta lista
  const coincideBusqueda = (p, texto) => {
    if (!texto) return true;
    const term = texto.toLowerCase();
    return (p.proyecto_nombre || '').toLowerCase().includes(term) ||
      (p.nombre_cliente || '').toLowerCase().includes(term) ||
      (p.apartamento || '').toLowerCase().includes(term);
  };

  const proyectosFiltrados = proyectos.filter(p => {
    const estadosPermitidos = ['en_ejecucion', 'entregado_con_detalles', 'cobrado_liquidado'];
    if (!estadosPermitidos.includes(p.estado)) return false;
    return coincideBusqueda(p, busquedaProyecto);
  });

  const especialesFiltrados = proyectos.filter(p =>
    p.estado === 'proyecto_especial' && coincideBusqueda(p, busquedaEspecial));

  if (loading) {
    return (
      <div className="fac-loading">
        <div className="fac-spinner"></div>
        <p>Cargando facturas...</p>
      </div>
    );
  }

  // Vista "Facturas sin clasificar" (migradas desde huerfanas).
  // Layout simplificado: solo header + tabs + componente dedicado.
  if (tipoVista === 'sin_clasificar') {
    return (
      <div className="fac-container">
        <div className="fac-header">
          <h2 className="fac-title">Facturas sin clasificar</h2>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {!tipoProp && (
              <div className="fac-tipo-tabs">
                <button className="fac-tipo-tab" onClick={() => setTipoVista('proyecto')}>Proyectos</button>
                <button className="fac-tipo-tab" onClick={() => setTipoVista('administrativa')}>Administrativas</button>
                <button className="fac-tipo-tab activa">Sin clasificar</button>
              </div>
            )}
          </div>
        </div>
        <FacturasSinClasificar />
      </div>
    );
  }

  return (
    <div className="fac-container">
      {/* Header */}
      <div className="fac-header">
        <h2 className="fac-title">{tipoVista === 'administrativa' ? 'Facturas Administrativas' : 'Facturas'}</h2>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {!tipoProp && (
            <div className="fac-tipo-tabs">
              <button className={`fac-tipo-tab${tipoVista === 'proyecto' ? ' activa' : ''}`} onClick={() => setTipoVista('proyecto')}>Proyectos</button>
              <button className={`fac-tipo-tab${tipoVista === 'administrativa' ? ' activa' : ''}`} onClick={() => setTipoVista('administrativa')}>Administrativas</button>
              <button className={`fac-tipo-tab${tipoVista === 'sin_clasificar' ? ' activa' : ''}`} onClick={() => setTipoVista('sin_clasificar')}>Sin clasificar</button>
            </div>
          )}
          <button className="fac-btn fac-btn-secondary" onClick={async () => {
            if (!window.confirm('¿Reparar archivos adjuntos de cuotas existentes?')) return;
            try {
              const r = await client.mutate(`mutation { repararGruposFacturas { success message } }`);
              alert(r.repararGruposFacturas.message);
              cargarFacturas();
            } catch { alert('Error al reparar'); }
          }} title="Propagar adjunto a todas las cuotas de planes de pago existentes">
            Reparar adjuntos
          </button>
          <button className="fac-btn fac-btn-primary" onClick={() => setMostrarFormulario(true)}>
            + Nueva Factura
          </button>
        </div>
      </div>

      {/* Stats */}
      {estadisticas && (
        <div className="fac-stats-grid">
          <div className="fac-stat-card">
            <span className="fac-stat-value">{estadisticas.total_facturas}</span>
            <span className="fac-stat-label">Total</span>
          </div>
          <div className="fac-stat-card fac-stat-monto">
            <span className="fac-stat-value">{formatMoney(estadisticas.monto_total)}</span>
            <span className="fac-stat-label">Facturado</span>
          </div>
          <div className="fac-stat-card fac-stat-pagado">
            <span className="fac-stat-value">{formatMoney(estadisticas.monto_pagado_total)}</span>
            <span className="fac-stat-label">Pagado</span>
          </div>
          <div className="fac-stat-card fac-stat-pendiente">
            <span className="fac-stat-value">{formatMoney(estadisticas.saldo_pendiente_total)}</span>
            <span className="fac-stat-label">Pendiente</span>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="fac-filters">
        <div className="fac-tabs">
          {['todos', 'pendiente', 'pagado_parcial', 'pagado', 'vencido'].map(estado => (
            <button
              key={estado}
              className={`fac-tab ${filtroEstado === estado ? 'activa' : ''}`}
              onClick={() => setFiltroEstado(estado)}
            >
              {estado === 'todos' && 'Todas'}
              {estado === 'pendiente' && 'Pendientes'}
              {estado === 'pagado_parcial' && 'Parciales'}
              {estado === 'pagado' && 'Pagadas'}
              {estado === 'vencido' && 'Vencidas'}
            </button>
          ))}
        </div>
        <input
          type="text"
          className="fac-search"
          placeholder="Buscar factura, proveedor, proyecto..."
          value={busqueda}
          onChange={e => setBusqueda(e.target.value)}
        />
      </div>

      {/* Report Tabs */}
      <div className="fac-report-tabs">
        <button className={`fac-report-tab ${reportTab === 'vencimiento' ? 'activa' : ''}`}
          onClick={() => setReportTab(reportTab === 'vencimiento' ? null : 'vencimiento')}>
          Por Fecha de Vencimiento
        </button>
        <button className={`fac-report-tab ${reportTab === 'proveedor' ? 'activa' : ''}`}
          onClick={() => setReportTab(reportTab === 'proveedor' ? null : 'proveedor')}>
          Por Proveedor
        </button>
        <button className={`fac-report-tab ${reportTab === 'proyecto' ? 'activa' : ''}`}
          onClick={() => setReportTab(reportTab === 'proyecto' ? null : 'proyecto')}>
          Por Proyecto
        </button>
      </div>

      {reportTab === 'vencimiento' && (
        <div className="fac-report-panel">
          <div className="fac-report-filters">
            <div className="fac-form-group">
              <label>Desde</label>
              <input type="date" value={filtroVencDesde} onChange={e => setFiltroVencDesde(e.target.value)} />
            </div>
            <div className="fac-form-group">
              <label>Hasta</label>
              <input type="date" value={filtroVencHasta} onChange={e => setFiltroVencHasta(e.target.value)} />
            </div>
            <button className="fac-btn fac-btn-export" onClick={exportarVencimiento}
              disabled={!filtroVencDesde || !filtroVencHasta}>
              Exportar Excel ({facturasVencFiltradas.length})
            </button>
          </div>
          {facturasVencFiltradas.length > 0 && (
            <div className="fac-report-preview">
              <table className="fac-report-table">
                <thead><tr><th>#</th><th>Proveedor</th><th>Proyecto</th><th>Apto</th><th>Vencimiento</th><th>Monto</th><th>Saldo</th><th>Estado</th></tr></thead>
                <tbody>
                  {facturasVencFiltradas.slice(0, 20).map(f => (
                    <tr key={f.id}>
                      <td>{f.numero_factura}</td><td>{f.proveedor_nombre}</td>
                      <td>{f.proyecto_nombre || '-'}</td><td>{f.apartamento || '-'}</td>
                      <td>{formatDate(f.fecha_vencimiento)}</td><td>{formatMoney(f.monto)}</td>
                      <td>{formatMoney(f.saldo_pendiente)}</td>
                      <td><span className={`fac-badge fac-badge-${f.estado}`}>{f.estado}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {facturasVencFiltradas.length > 20 && <p className="fac-report-more">... y {facturasVencFiltradas.length - 20} mas en el Excel</p>}
              <div className="fac-report-total">Total: {formatMoney(facturasVencFiltradas.reduce((s, f) => s + (parseFloat(f.monto) || 0), 0))} | Saldo: {formatMoney(facturasVencFiltradas.reduce((s, f) => s + (parseFloat(f.saldo_pendiente) || 0), 0))}</div>
            </div>
          )}
        </div>
      )}

      {reportTab === 'proveedor' && (
        <div className="fac-report-panel">
          <div className="fac-report-filters">
            <div className="fac-form-group" style={{ minWidth: 250, position: 'relative' }}>
              <label>Proveedor</label>
              <input type="text" placeholder="Buscar proveedor..."
                value={filtroProvId ? (proveedores.find(p => String(p.id) === String(filtroProvId))?.nombre || '') : (reportProvBusqueda || '')}
                onChange={e => {
                  const val = e.target.value;
                  setReportProvBusqueda(val);
                  if (!val) setFiltroProvId('');
                }}
                onFocus={() => setShowReportProvDrop(true)}
                onBlur={() => setTimeout(() => setShowReportProvDrop(false), 200)}
              />
              {filtroProvId && <button type="button" style={{ position: 'absolute', right: 8, top: 28, background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }} onClick={() => { setFiltroProvId(''); setReportProvBusqueda(''); }}>x</button>}
              {showReportProvDrop && (
                <div className="fac-proveedor-dropdown" style={{ maxHeight: 200, zIndex: 10 }}>
                  {proveedores.filter(p => !reportProvBusqueda || p.nombre.toLowerCase().includes((reportProvBusqueda || '').toLowerCase())).slice(0, 15).map(p => (
                    <div key={p.id} className="fac-proveedor-option" onClick={() => { setFiltroProvId(String(p.id)); setReportProvBusqueda(''); setShowReportProvDrop(false); }}>
                      <strong>{p.nombre}</strong>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="fac-form-group">
              <label>Desde</label>
              <input type="date" value={filtroProvFechaDesde} onChange={e => setFiltroProvFechaDesde(e.target.value)} />
            </div>
            <div className="fac-form-group">
              <label>Hasta</label>
              <input type="date" value={filtroProvFechaHasta} onChange={e => setFiltroProvFechaHasta(e.target.value)} />
            </div>
            <button className="fac-btn fac-btn-export" onClick={exportarProveedor}
              disabled={!filtroProvId || !filtroProvFechaDesde || !filtroProvFechaHasta}>
              Exportar Excel ({facturasProvFiltradas.length})
            </button>
          </div>
          {facturasProvFiltradas.length > 0 && (
            <div className="fac-report-preview">
              <table className="fac-report-table">
                <thead><tr><th>#</th><th>Proyecto</th><th>Apto</th><th>Descripcion</th><th>Emision</th><th>Vencimiento</th><th>Monto</th><th>Saldo</th><th>Estado</th></tr></thead>
                <tbody>
                  {facturasProvFiltradas.slice(0, 20).map(f => (
                    <tr key={f.id}>
                      <td>{f.numero_factura}</td><td>{f.proyecto_nombre || '-'}</td>
                      <td>{f.apartamento || '-'}</td><td>{f.descripcion || '-'}</td>
                      <td>{formatDate(f.fecha_emision)}</td><td>{formatDate(f.fecha_vencimiento)}</td>
                      <td>{formatMoney(f.monto)}</td><td>{formatMoney(f.saldo_pendiente)}</td>
                      <td><span className={`fac-badge fac-badge-${f.estado}`}>{f.estado}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {facturasProvFiltradas.length > 20 && <p className="fac-report-more">... y {facturasProvFiltradas.length - 20} mas en el Excel</p>}
              <div className="fac-report-total">Total: {formatMoney(facturasProvFiltradas.reduce((s, f) => s + (parseFloat(f.monto) || 0), 0))} | Saldo: {formatMoney(facturasProvFiltradas.reduce((s, f) => s + (parseFloat(f.saldo_pendiente) || 0), 0))}</div>
            </div>
          )}
        </div>
      )}

      {reportTab === 'proyecto' && (
        <div className="fac-report-panel">
          <div className="fac-report-filters">
            <div className="fac-form-group" style={{ minWidth: 300, position: 'relative' }}>
              <label>Proyecto</label>
              <input type="text" placeholder="Buscar proyecto, apto o cliente..."
                value={filtroProyId ? (() => {
                  const p = proyectosUnicos.find(x => String(x.id) === String(filtroProyId));
                  return p ? `${p.nombre}${p.apartamento ? ' · Apto ' + p.apartamento : ''}${p.nombre_cliente ? ' · ' + p.nombre_cliente : ''}` : '';
                })() : (reportProyBusqueda || '')}
                onChange={e => {
                  const val = e.target.value;
                  setReportProyBusqueda(val);
                  if (!val) setFiltroProyId('');
                }}
                onFocus={() => setShowReportProyDrop(true)}
                onBlur={() => setTimeout(() => setShowReportProyDrop(false), 200)}
              />
              {filtroProyId && <button type="button" style={{ position: 'absolute', right: 8, top: 28, background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }} onClick={() => { setFiltroProyId(''); setReportProyBusqueda(''); }}>x</button>}
              {showReportProyDrop && (
                <div className="fac-proveedor-dropdown" style={{ maxHeight: 240, zIndex: 10 }}>
                  {proyectosUnicos.filter(p => {
                    const q = (reportProyBusqueda || '').toLowerCase();
                    if (!q) return true;
                    return (p.nombre || '').toLowerCase().includes(q)
                        || (p.apartamento || '').toLowerCase().includes(q)
                        || (p.nombre_cliente || '').toLowerCase().includes(q);
                  }).slice(0, 30).map(p => (
                    <div key={p.id} className="fac-proveedor-option" onClick={() => { setFiltroProyId(String(p.id)); setReportProyBusqueda(''); setShowReportProyDrop(false); }}>
                      <strong>{p.nombre}</strong>
                      {(p.apartamento || p.nombre_cliente) && (
                        <span style={{ color: '#64748b', marginLeft: 6, fontSize: '0.85em' }}>
                          {p.apartamento && `· Apto ${p.apartamento}`} {p.nombre_cliente && `· ${p.nombre_cliente}`}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="fac-form-group">
              <label>Desde</label>
              <input type="date" value={filtroProyFechaDesde} onChange={e => setFiltroProyFechaDesde(e.target.value)} />
            </div>
            <div className="fac-form-group">
              <label>Hasta</label>
              <input type="date" value={filtroProyFechaHasta} onChange={e => setFiltroProyFechaHasta(e.target.value)} />
            </div>
            <button className="fac-btn fac-btn-export" onClick={exportarProyecto}
              disabled={!filtroProyId || !filtroProyFechaDesde || !filtroProyFechaHasta}>
              Exportar Excel ({facturasProyFiltradas.length})
            </button>
          </div>
          {facturasProyFiltradas.length > 0 && (
            <div className="fac-report-preview">
              <table className="fac-report-table">
                <thead><tr><th>#</th><th>Proveedor</th><th>Apto</th><th>Descripcion</th><th>Emision</th><th>Vencimiento</th><th>Monto</th><th>Saldo</th><th>Estado</th></tr></thead>
                <tbody>
                  {facturasProyFiltradas.slice(0, 20).map(f => (
                    <tr key={f.id}>
                      <td>{f.numero_factura}</td><td>{f.proveedor_nombre || '-'}</td>
                      <td>{f.apartamento || '-'}</td><td>{f.descripcion || '-'}</td>
                      <td>{formatDate(f.fecha_emision)}</td><td>{formatDate(f.fecha_vencimiento)}</td>
                      <td>{formatMoney(f.monto)}</td><td>{formatMoney(f.saldo_pendiente)}</td>
                      <td><span className={`fac-badge fac-badge-${f.estado}`}>{f.estado}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {facturasProyFiltradas.length > 20 && <p className="fac-report-more">... y {facturasProyFiltradas.length - 20} mas en el Excel</p>}
              <div className="fac-report-total">Total: {formatMoney(facturasProyFiltradas.reduce((s, f) => s + (parseFloat(f.monto) || 0), 0))} | Saldo: {formatMoney(facturasProyFiltradas.reduce((s, f) => s + (parseFloat(f.saldo_pendiente) || 0), 0))}</div>
            </div>
          )}
        </div>
      )}

      {/* Invoices list */}
      {facturasFiltradas.length === 0 ? (
        <div className="fac-empty">No hay facturas para mostrar</div>
      ) : (
        <div className="fac-list">
          {facturasAgrupadas.map(grupo => {
            // Grupo multi-proyecto: mostrar card agrupado
            if (grupo.esGrupo && grupo.facturas.length > 1) {
              const gid = grupo.facturas[0].grupo_factura_id;
              const expandido = !!gruposExpandidos[gid];
              const totalGrupo = grupo.facturas.reduce((s, f) => s + (parseFloat(f.monto) || 0), 0);
              const totalPagadoGrupo = grupo.facturas.reduce((s, f) => s + (parseFloat(f.monto_pagado) || 0), 0);
              const totalRetGrupo = grupo.facturas.reduce((s, f) =>
                s + (parseFloat(f.retencion_fuente) || 0) + (parseFloat(f.retencion_iva) || 0) + (parseFloat(f.otras_retenciones) || 0), 0);
              const netoGrupo = totalGrupo - totalRetGrupo;
              const primera = grupo.facturas[0];
              const archivoCompartido = primera.archivo_factura;
              const estadosGrupo = new Set(grupo.facturas.map(f => {
                const ev = f.dias_vencido > 0 && f.estado !== 'pagado';
                return ev ? 'vencido' : f.estado;
              }));
              const estadoAgrupado = estadosGrupo.size === 1 ? Array.from(estadosGrupo)[0] : 'mixto';

              return (
                <div key={grupo.key} className={`fac-item fac-grupo fac-estado-${estadoAgrupado}`}>
                  <div
                    className="fac-grupo-header"
                    onClick={() => setGruposExpandidos(prev => ({ ...prev, [gid]: !prev[gid] }))}
                    style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12, padding: '4px 0' }}
                  >
                    <span style={{ fontSize: '1rem', width: 18, display: 'inline-block', textAlign: 'center' }}>
                      {expandido ? '▼' : '▶'}
                    </span>
                    <div className="fac-item-left" style={{ flex: 1 }}>
                      <div className="fac-item-numero">
                        <strong>#{primera.numero_factura}</strong>
                        <span className="fac-badge" style={{ background: '#e0e7ff', color: '#4338ca', fontWeight: 700 }}>
                          🔗 {grupo.facturas.length} proyectos
                        </span>
                        {estadoAgrupado !== 'mixto' && (
                          <span className={`fac-badge fac-badge-${estadoAgrupado}`}>
                            {estadoAgrupado === 'pendiente' && 'Pendiente'}
                            {estadoAgrupado === 'pagado_parcial' && 'Parcial'}
                            {estadoAgrupado === 'pagado' && 'Pagado'}
                            {estadoAgrupado === 'vencido' && 'Vencida'}
                            {estadoAgrupado === 'cancelado' && 'Cancelada'}
                          </span>
                        )}
                        {primera.categoria && <span className={`fac-badge fac-badge-concepto fac-concepto-${primera.categoria}`}>{CONCEPTOS_FACTURA.find(c => c.value === primera.categoria)?.label || primera.categoria}</span>}
                      </div>
                      <div className="fac-item-meta">
                        <span className="fac-item-proveedor">{primera.proveedor_nombre}</span>
                      </div>
                      {primera.descripcion && (
                        <div className="fac-item-desc">{primera.descripcion}</div>
                      )}
                      <div className="fac-item-fechas">
                        <span>Emisión: {formatDate(primera.fecha_emision)}</span>
                        {primera.fecha_vencimiento && <span>Vence: {formatDate(primera.fecha_vencimiento)}</span>}
                      </div>
                    </div>
                    <div className="fac-item-right">
                      <div className="fac-item-monto">{formatMoney(totalGrupo)}</div>
                      {totalRetGrupo > 0 && <div className="fac-item-neto">Neto: {formatMoney(netoGrupo)}</div>}
                      {totalPagadoGrupo > 0 && estadoAgrupado !== 'pagado' && (
                        <div className="fac-item-saldo">
                          Pagado: {formatMoney(totalPagadoGrupo)} | Saldo: {formatMoney(totalGrupo - totalPagadoGrupo)}
                        </div>
                      )}
                    </div>
                    <div className="fac-item-actions" onClick={e => e.stopPropagation()}>
                      {archivoCompartido && (
                        <button
                          className="fac-btn-icon"
                          onClick={() => window.open(`${UPLOAD_URL}/factura-file/${encodeURIComponent(archivoCompartido)}`, '_blank')}
                          title="Ver adjunto (compartido por el grupo)"
                        >
                          📎
                        </button>
                      )}
                    </div>
                  </div>

                  {expandido && (
                    <div className="fac-grupo-body" style={{ marginTop: 8, paddingLeft: 28, borderLeft: '2px solid #c7d2fe' }}>
                      {grupo.facturas.map(factura => {
                        const esVencida = factura.dias_vencido > 0 && factura.estado !== 'pagado';
                        const estadoFinal = esVencida ? 'vencido' : factura.estado;
                        const retFuente = parseFloat(factura.retencion_fuente) || 0;
                        const retIva = parseFloat(factura.retencion_iva) || 0;
                        const otrasRet = parseFloat(factura.otras_retenciones) || 0;
                        const totalRet = retFuente + retIva + otrasRet;
                        const valorNeto = (parseFloat(factura.monto) || 0) - totalRet;

                        return (
                          <div key={factura.id} className={`fac-item fac-item-sub fac-estado-${estadoFinal}`} style={{ marginBottom: 6 }}>
                            <div className="fac-item-main">
                              <div className="fac-item-left">
                                <div className="fac-item-numero">
                                  <span className={`fac-badge fac-badge-${estadoFinal}`}>
                                    {estadoFinal === 'pendiente' && 'Pendiente'}
                                    {estadoFinal === 'pagado_parcial' && 'Parcial'}
                                    {estadoFinal === 'pagado' && 'Pagado'}
                                    {estadoFinal === 'vencido' && 'Vencida'}
                                    {estadoFinal === 'cancelado' && 'Cancelada'}
                                  </span>
                                  {esVencida && <span className="fac-dias-badge">{factura.dias_vencido}d</span>}
                                  {factura.categoria && <span className={`fac-badge fac-badge-concepto fac-concepto-${factura.categoria}`}>{CONCEPTOS_FACTURA.find(c => c.value === factura.categoria)?.label || factura.categoria}</span>}
                                </div>
                                <div className="fac-item-meta">
                                  <span className="fac-item-proyecto">{factura.proyecto_nombre || 'Sin proyecto'}{factura.apartamento ? ` - Apto ${factura.apartamento}` : ''}</span>
                                </div>
                              </div>
                              <div className="fac-item-right">
                                <div className="fac-item-monto">{formatMoney(factura.monto)}</div>
                                {totalRet > 0 && <div className="fac-item-neto">Neto: {formatMoney(valorNeto)}</div>}
                                {factura.monto_pagado > 0 && factura.estado !== 'pagado' && (
                                  <div className="fac-item-saldo">
                                    Pagado: {formatMoney(factura.monto_pagado)} | Saldo: {formatMoney(factura.saldo_pendiente)}
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="fac-item-actions">
                              <button
                                className="fac-btn-icon fac-btn-edit"
                                onClick={() => abrirEdicion(factura)}
                                title="Editar"
                              >
                                ✏️
                              </button>
                              {factura.estado !== 'pagado' && (
                                <button
                                  className="fac-btn-icon fac-btn-danger"
                                  onClick={() => handleEliminar(factura)}
                                  disabled={eliminandoFactura === factura.id}
                                  title="Eliminar"
                                >
                                  {eliminandoFactura === factura.id ? '...' : '🗑️'}
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }

            // Factura normal (un solo proyecto o grupo de 1)
            const factura = grupo.facturas[0];
            const esVencida = factura.dias_vencido > 0 && factura.estado !== 'pagado';
            const estadoFinal = esVencida ? 'vencido' : factura.estado;
            const retFuente = parseFloat(factura.retencion_fuente) || 0;
            const retIva = parseFloat(factura.retencion_iva) || 0;
            const otrasRet = parseFloat(factura.otras_retenciones) || 0;
            const totalRet = retFuente + retIva + otrasRet;
            const valorNeto = (parseFloat(factura.monto) || 0) - totalRet;

            return (
              <div key={factura.id} className={`fac-item fac-estado-${estadoFinal}`}>
                <div className="fac-item-main">
                  <div className="fac-item-left">
                    <div className="fac-item-numero">
                      <strong>#{factura.numero_factura}</strong>
                      <span className={`fac-badge fac-badge-${estadoFinal}`}>
                        {estadoFinal === 'pendiente' && 'Pendiente'}
                        {estadoFinal === 'pagado_parcial' && 'Parcial'}
                        {estadoFinal === 'pagado' && 'Pagado'}
                        {estadoFinal === 'vencido' && 'Vencida'}
                        {estadoFinal === 'cancelado' && 'Cancelada'}
                      </span>
                      {esVencida && <span className="fac-dias-badge">{factura.dias_vencido}d</span>}
                      {factura.es_posventa && <span className="fac-badge" style={{ background: '#ccfbf1', color: '#0f766e' }}>🛠️ Posventa</span>}
                      {factura.categoria && <span className={`fac-badge fac-badge-concepto fac-concepto-${factura.categoria}`}>{CONCEPTOS_FACTURA.find(c => c.value === factura.categoria)?.label || factura.categoria}</span>}
                    </div>
                    <div className="fac-item-meta">
                      <span className="fac-item-proveedor">{factura.proveedor_nombre}</span>
                      <span className="fac-item-sep">|</span>
                      <span className="fac-item-proyecto">{factura.proyecto_nombre || 'Sin proyecto'}{factura.apartamento ? ` - Apto ${factura.apartamento}` : ''}</span>
                    </div>
                    {factura.descripcion && (
                      <div className="fac-item-desc">{factura.descripcion}</div>
                    )}
                    <div className="fac-item-fechas">
                      <span>Emisión: {formatDate(factura.fecha_emision)}</span>
                      {factura.fecha_vencimiento && (
                        <span>Vence: {formatDate(factura.fecha_vencimiento)}</span>
                      )}
                    </div>
                  </div>

                  <div className="fac-item-right">
                    <div className="fac-item-monto">{formatMoney(factura.monto)}</div>
                    {totalRet > 0 && (
                      <div className="fac-item-neto">Neto: {formatMoney(valorNeto)}</div>
                    )}
                    {factura.monto_pagado > 0 && factura.estado !== 'pagado' && (
                      <div className="fac-item-saldo">
                        Pagado: {formatMoney(factura.monto_pagado)} | Saldo: {formatMoney(factura.saldo_pendiente)}
                      </div>
                    )}
                  </div>
                </div>

                <div className="fac-item-actions">
                  {factura.archivo_factura && (
                    <button
                      className="fac-btn-icon"
                      onClick={() => {
                        window.open(`${UPLOAD_URL}/factura-file/${encodeURIComponent(factura.archivo_factura)}`, '_blank');
                      }}
                      title="Ver adjunto"
                    >
                      📎
                    </button>
                  )}
                  <button
                    className="fac-btn-icon"
                    onClick={() => togglePosventa(factura)}
                    title={factura.es_posventa ? 'Quitar posventa (volver a obra)' : 'Marcar como posventa'}
                    style={factura.es_posventa ? { background: '#ccfbf1', borderColor: '#5eead4' } : undefined}
                  >
                    🛠️
                  </button>
                  <button
                    className="fac-btn-icon fac-btn-edit"
                    onClick={() => abrirEdicion(factura)}
                    title="Editar"
                  >
                    ✏️
                  </button>
                  {factura.estado !== 'pagado' && (
                    <button
                      className="fac-btn-icon fac-btn-danger"
                      onClick={() => handleEliminar(factura)}
                      disabled={eliminandoFactura === factura.id}
                      title="Eliminar"
                    >
                      {eliminandoFactura === factura.id ? '...' : '🗑️'}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Creation form modal */}
      {mostrarFormulario && (
        <div className="fac-modal-overlay" onClick={() => setMostrarFormulario(false)}>
          <div className="fac-modal" onClick={e => e.stopPropagation()}>
            <div className="fac-modal-header">
              <h3>{facturaEditandoId ? 'Editar Factura' : 'Nueva Factura'}</h3>
              <button className="fac-modal-close" onClick={() => { setMostrarFormulario(false); resetFormulario(); }}>X</button>
            </div>

            <form onSubmit={handleSubmit} className="fac-form">
              <div className="fac-form-scroll">
                {/* Row 1: Fecha + Numero */}
                <div className="fac-form-row">
                  <div className="fac-form-group">
                    <label>Fecha Emisión *</label>
                    <input type="date" value={formData.fecha_emision}
                      onChange={e => setFormData(prev => ({ ...prev, fecha_emision: e.target.value }))} required />
                  </div>
                  <div className="fac-form-group">
                    <label>No. Factura *</label>
                    <input type={facturaEditandoId ? 'text' : 'number'} value={formData.numero_factura} placeholder="Ej: 001"
                      onChange={e => {
                        const val = e.target.value;
                        setFormData(prev => ({ ...prev, numero_factura: val }));
                        validarFacturaDuplicada(val, formData.proveedor_id);
                      }} required
                      className={facturaExiste ? 'input-error' : ''} />
                    {facturaExiste && <span className="fac-error-msg">Este número de factura ya existe para este proveedor</span>}
                  </div>
                  <div className="fac-form-group">
                    <label>Fecha Vencimiento *</label>
                    <input type="date" value={formData.fecha_vencimiento}
                      onChange={e => setFormData(prev => ({ ...prev, fecha_vencimiento: e.target.value }))} />
                  </div>
                </div>

                {/* Row 2: Proveedor con búsqueda */}
                <div className="fac-form-row">
                  <div className="fac-form-group fac-form-grow" ref={proveedorDropdownRef} style={{ position: 'relative' }}>
                    <label>Proveedor *</label>
                    <input
                      type="text"
                      placeholder="Buscar proveedor..."
                      value={busquedaProveedor}
                      onChange={(e) => { setBusquedaProveedor(e.target.value); setShowProveedorDropdown(true); }}
                      onFocus={() => setShowProveedorDropdown(true)}
                      autoComplete="off"
                    />
                    {formData.proveedor_id && !showProveedorDropdown && (
                      <div className="fac-proveedor-selected">
                        {proveedores.find(p => String(p.id) === String(formData.proveedor_id))?.nombre || ''}
                        <button type="button" className="fac-proveedor-clear" onClick={() => {
                          setFormData(prev => ({ ...prev, proveedor_id: '' }));
                          setProveedorSeleccionado(null);
                          setBusquedaProveedor('');
                          setFacturaExiste(false);
                        }}>✕</button>
                      </div>
                    )}
                    {showProveedorDropdown && (
                      <div className="fac-proveedor-dropdown">
                        {proveedores
                          .filter(p => {
                            if (!busquedaProveedor) return true;
                            const q = busquedaProveedor.toLowerCase();
                            return p.nombre?.toLowerCase().includes(q) || p.nit?.toLowerCase().includes(q);
                          })
                          .map(p => (
                            <div key={p.id} className="fac-proveedor-option" onClick={() => {
                              handleProveedorChange(p.id);
                              setBusquedaProveedor(p.nombre);
                              setShowProveedorDropdown(false);
                              validarFacturaDuplicada(formData.numero_factura, p.id);
                            }}>
                              <strong>{p.nombre}</strong>
                              {p.nit && <span className="fac-prov-nit">{p.nit}</span>}
                            </div>
                          ))
                        }
                        {proveedores.filter(p => {
                          if (!busquedaProveedor) return true;
                          const q = busquedaProveedor.toLowerCase();
                          return p.nombre?.toLowerCase().includes(q) || p.nit?.toLowerCase().includes(q);
                        }).length === 0 && <div className="fac-proveedor-option fac-prov-empty">Sin resultados</div>}
                      </div>
                    )}
                  </div>
                </div>

                {/* Retention info — clickable cuando hay varias */}
                {proveedorSeleccionado?.retenciones?.length > 0 && (
                  <div className="fac-retenciones-info">
                    <span className="fac-ret-label">
                      Retenciones{proveedorSeleccionado.retenciones.length > 1 ? ' (elige una):' : ':'}
                    </span>
                    {proveedorSeleccionado.retenciones.map((ret, i) => {
                      const retId = ret.tipo_retencion_id ?? ret.id;
                      const seleccionada = retId === retencionSeleccionadaIdFact;
                      const esClicable = proveedorSeleccionado.retenciones.length > 1;
                      return (
                        <button
                          type="button"
                          key={i}
                          className={`fac-ret-tag ${seleccionada ? 'fac-ret-tag-active' : ''}`}
                          onClick={() => esClicable && setRetencionSeleccionadaIdFact(retId)}
                          style={{
                            cursor: esClicable ? 'pointer' : 'default',
                            border: seleccionada ? '2px solid #0ea5e9' : '1px solid #cbd5e1',
                            background: seleccionada ? '#e0f2fe' : '#f1f5f9',
                          }}
                        >
                          {seleccionada && esClicable ? '✓ ' : ''}
                          {ret.tipo_retencion_nombre}: {ret.porcentaje_retencion}%
                          {ret.es_principal == 1 && ' (Ppal)'}
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Row 3: Descripcion (Concepto se selecciona en Plan de pagos por capítulo) */}
                <div className="fac-form-row">
                  <div className="fac-form-group fac-form-grow">
                    <label>Descripcion{tipoVista === 'administrativa' ? ' *' : ''}</label>
                    <input type="text" value={formData.descripcion} placeholder={tipoVista === 'administrativa' ? "Descripcion del gasto administrativo..." : "Descripcion de la factura (opcional)..."}
                      onChange={e => setFormData(prev => ({ ...prev, descripcion: e.target.value }))} />
                  </div>
                </div>

                {/* Concepto a nivel de factura — visible en edición (con o sin plan de pagos) */}
                {facturaEditandoId && tipoVista !== 'administrativa' && (() => {
                  // Construir lista de conceptos disponibles (BD o fallback)
                  let opciones = [];
                  if (conceptosBD.length === 0) {
                    opciones = CONCEPTOS_FACTURA.map(c => ({ value: c.value, label: c.label }));
                  } else {
                    conceptosBD.forEach(cap => (cap.conceptos || [])
                      .filter(c => c.activo !== false)
                      .forEach(c => opciones.push({ value: c.nombre, label: c.nombre })));
                  }
                  // Deduplicar por value
                  const vistos = new Set();
                  opciones = opciones.filter(o => (vistos.has(o.value) ? false : (vistos.add(o.value), true)));
                  // Garantizar que el concepto ACTUAL esté presente y seleccionable, aunque
                  // su formato no coincida exacto con las opciones (ej. valores antiguos en minúscula)
                  const actual = formData.categoria || '';
                  if (actual && !vistos.has(actual)) {
                    const lbl = CONCEPTOS_FACTURA.find(c => c.value === actual)?.label || actual;
                    opciones.unshift({ value: actual, label: lbl });
                  }
                  return (
                    <div className="fac-form-row">
                      <div className="fac-form-group fac-form-grow">
                        <label>Concepto</label>
                        <select
                          className="fac-capitulo-concepto"
                          value={actual}
                          onChange={e => setFormData(prev => ({ ...prev, categoria: e.target.value }))}
                        >
                          <option value="">Sin concepto</option>
                          {opciones.map((o, i) => (
                            <option key={`${o.value}-${i}`} value={o.value}>{o.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  );
                })()}

                {/* Row 4: Total Factura */}
                <div className="fac-form-row">
                  <div className="fac-form-group">
                    <label>Total Factura *</label>
                    <input type="number" value={formData.subtotal} placeholder="0"
                      onChange={e => setFormData(prev => ({ ...prev, subtotal: e.target.value, porcentaje_iva: '0' }))} required min="0" />
                  </div>
                </div>

                {/* Calculations preview */}
                {parseFloat(formData.subtotal) > 0 && (
                  <div className="fac-calculos">
                    <div className="fac-calc-row fac-calc-total">
                      <span>Total Factura:</span>
                      <span>{formatMoney(parseFloat(formData.subtotal))}</span>
                    </div>
                    {calculos.retencion_fuente > 0 && (
                      <div className="fac-calc-row fac-calc-ret">
                        <span>(-) Ret. Fuente:</span>
                        <span>-{formatMoney(calculos.retencion_fuente)}</span>
                      </div>
                    )}
                    {calculos.retencion_iva > 0 && (
                      <div className="fac-calc-row fac-calc-ret">
                        <span>(-) Ret. IVA:</span>
                        <span>-{formatMoney(calculos.retencion_iva)}</span>
                      </div>
                    )}
                    {calculos.total_retenciones > 0 && (
                      <div className="fac-calc-row fac-calc-neto">
                        <span>Valor Neto a Pagar:</span>
                        <span>{formatMoney(calculos.valor_neto_pagar)}</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Project distribution - solo para facturas de proyecto */}
                {tipoVista !== 'administrativa' && (
                <div className="fac-section">
                  <div className="fac-section-header">
                    <h4>Proyectos *</h4>
                    {proyectosDistribucion.length > 1 && (
                      <button type="button" className="fac-btn fac-btn-sm fac-btn-secondary" onClick={distribuirEquitativamente}>
                        Distribuir equitativamente
                      </button>
                    )}
                  </div>

                  {/* Tabs: Proyectos | Posventa */}
                  <div className="fac-proy-tabs">
                    <button type="button" className={`fac-proy-tab ${tabProyecto === 'proyectos' ? 'activa' : ''}`} onClick={() => setTabProyecto('proyectos')}>
                      Proyectos en Ejecucion
                    </button>
                    <button type="button" className={`fac-proy-tab ${tabProyecto === 'posventa' ? 'activa' : ''}`} onClick={() => setTabProyecto('posventa')}>
                      Posventa
                    </button>
                    <button type="button" className={`fac-proy-tab ${tabProyecto === 'especiales' ? 'activa' : ''}`} onClick={() => setTabProyecto('especiales')}>
                      Proyectos Especiales
                    </button>
                  </div>

                  {/* Tab: Proyectos normales */}
                  {tabProyecto === 'proyectos' && (
                    <div className="fac-proyecto-search">
                      <input
                        type="text"
                        placeholder="Buscar proyecto por nombre, cliente o apto..."
                        value={busquedaProyecto}
                        onChange={e => { setBusquedaProyecto(e.target.value); setMostrarListaProyectos(true); }}
                        onFocus={() => setMostrarListaProyectos(true)}
                      />
                      {mostrarListaProyectos && (
                        <div className="fac-proyecto-dropdown">
                          {proyectosFiltrados.slice(0, 10).map(p => (
                            <div
                              key={p.id}
                              className={`fac-proyecto-option ${proyectosDistribucion.find(pd => pd.proyecto_id === p.id) ? 'disabled' : ''}`}
                              onClick={() => !proyectosDistribucion.find(pd => pd.proyecto_id === p.id) && agregarProyecto(p)}
                            >
                              <strong>{p.proyecto_nombre}</strong>
                              <span>{p.nombre_cliente} {p.apartamento ? `- Apto ${p.apartamento}` : ''}</span>
                            </div>
                          ))}
                          {proyectosFiltrados.length === 0 && (
                            <div className="fac-proyecto-option disabled">No se encontraron proyectos</div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Tab: Proyectos Especiales — mismo flujo que un proyecto de obra */}
                  {tabProyecto === 'especiales' && (
                    <div className="fac-proyecto-search">
                      <input
                        type="text"
                        placeholder="Buscar proyecto especial por nombre, propietario o apto..."
                        value={busquedaEspecial}
                        onChange={e => { setBusquedaEspecial(e.target.value); setMostrarListaEspeciales(true); }}
                        onFocus={() => setMostrarListaEspeciales(true)}
                      />
                      {mostrarListaEspeciales && (
                        <div className="fac-proyecto-dropdown">
                          {especialesFiltrados.slice(0, 20).map(p => {
                            const yaEsta = !!proyectosDistribucion.find(pd => pd.proyecto_id === p.id);
                            return (
                              <div
                                key={p.id}
                                className={`fac-proyecto-option ${yaEsta ? 'disabled' : ''}`}
                                onClick={() => { if (!yaEsta) { agregarProyecto(p); setMostrarListaEspeciales(false); setBusquedaEspecial(''); } }}
                              >
                                <strong>{p.proyecto_nombre}</strong>
                                <span>
                                  {p.nombre_cliente} {p.apartamento ? `- Apto ${p.apartamento}` : ''}
                                  {' '}<span className="fac-pv-badge">Especial</span>
                                </span>
                              </div>
                            );
                          })}
                          {especialesFiltrados.length === 0 && (
                            <div className="fac-proyecto-option disabled">No se encontraron proyectos especiales</div>
                          )}
                        </div>
                      )}
                      <p style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: 6 }}>
                        Lo que se asigne aquí suma al <b>valor ejecutado</b> del proyecto especial.
                      </p>
                    </div>
                  )}

                  {/* Tab: Posventa — busca CUALQUIER apartamento; la factura se marca como posventa */}
                  {tabProyecto === 'posventa' && (
                    <div className="fac-proyecto-search">
                      <input
                        type="text"
                        placeholder="Buscar apartamento por proyecto, cliente o # apto..."
                        value={busquedaPosventa}
                        onChange={e => { setBusquedaPosventa(e.target.value); setMostrarListaPosventa(true); }}
                        onFocus={() => setMostrarListaPosventa(true)}
                      />
                      {mostrarListaPosventa && (() => {
                        // Lista = TODO lo del módulo de posventa (por nombre + # apto). Al elegir,
                        // se resuelve/crea la entrada posventa separada (estado='posventa') y la
                        // factura se carga ahí, nunca a un proyecto de obra real.
                        const t = (busquedaPosventa || '').toLowerCase();
                        const pvFiltrados = posventaList.filter(p =>
                          !t || (p.proyecto_nombre || '').toLowerCase().includes(t) ||
                            (p.apartamento || '').toLowerCase().includes(t));
                        const seleccionar = async (p) => {
                          try {
                            const r = await client.mutate(
                              `mutation($n: String!, $a: String!) { resolverAptoPosventa(proyecto_nombre: $n, apartamento: $a) { success proyecto_id message } }`,
                              { n: p.proyecto_nombre, a: p.apartamento });
                            const res = r.resolverAptoPosventa;
                            if (!res?.success || !res.proyecto_id) { showError(res?.message || 'No se pudo resolver el apartamento posventa'); return; }
                            if (!proyectosDistribucion.find(pd => pd.proyecto_id === res.proyecto_id)) {
                              agregarProyecto({ id: res.proyecto_id, proyecto_nombre: p.proyecto_nombre, apartamento: p.apartamento, nombre_cliente: '', es_posventa: true });
                            }
                            setMostrarListaPosventa(false);
                            setBusquedaPosventa('');
                          } catch { showError('Error al seleccionar el apartamento posventa'); }
                        };
                        return (
                          <div className="fac-proyecto-dropdown">
                            {pvFiltrados.slice(0, 20).map((p, idx) => (
                              <div
                                key={`${p.proyecto_nombre}|${p.apartamento}|${idx}`}
                                className="fac-proyecto-option"
                                onClick={() => seleccionar(p)}
                              >
                                <strong>{p.proyecto_nombre}</strong>
                                <span>{p.apartamento ? `Apto ${p.apartamento}` : ''} <span className="fac-pv-badge">Posventa</span></span>
                              </div>
                            ))}
                            {pvFiltrados.length === 0 && (
                              <div className="fac-proyecto-option disabled">No se encontraron apartamentos posventa</div>
                            )}
                          </div>
                        );
                      })()}
                      <p style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: 6 }}>
                        La factura quedará marcada como <b>posventa</b> (aparte del presupuesto de obra).
                      </p>
                    </div>
                  )}

                  {/* Selected projects with distribution */}
                  {proyectosDistribucion.map(pd => {
                    const totalCuotas = pd.cuotas?.reduce((sum, c) => sum + (parseFloat(c.monto) || 0), 0) || 0;
                    return (
                      <div key={pd.proyecto_id} className="fac-dist-item">
                        <div className="fac-dist-header">
                          <span className="fac-dist-nombre">{pd.nombre}</span>
                          <button type="button" className="fac-btn-icon fac-btn-danger" onClick={() => quitarProyecto(pd.proyecto_id)}>X</button>
                        </div>
                        <div className="fac-dist-row">
                          <div className="fac-form-group">
                            <label>Valor asignado</label>
                            <input
                              type="number"
                              value={pd.valor}
                              placeholder="0"
                              onChange={e => actualizarValorProyecto(pd.proyecto_id, e.target.value)}
                              min="0"
                            />
                          </div>
                          <div className="fac-form-group fac-plan-toggle">
                            <label>
                              <input type="checkbox" checked={pd.plan_pagos} onChange={() => togglePlanPagos(pd.proyecto_id)} />
                              Plan de pagos
                            </label>
                          </div>
                        </div>

                        {/* Capítulos del proyecto */}
                        {pd.capitulos && pd.capitulos.length > 0 && (() => {
                          const norm = (s) => (s || '').toString().normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim().replace(/\s+/g, ' ');
                          return (
                          <div className="fac-capitulos-section">
                            <label className="fac-capitulos-label">Capítulos asociados: <span style={{ fontWeight: 400, color: '#64748b', fontSize: '0.78rem' }}>(disponible = (valor ÷ 1.15) − ejecutado)</span></label>
                            <div className="fac-capitulos-grid">
                              {pd.capitulos.map(cap => {
                                const seleccionado = (pd.capitulos_seleccionados || []).includes(cap.id);
                                // Buscar el capítulo correspondiente en Conceptos Facturas (BD)
                                const capBD = conceptosBD.find(cb => norm(cb.nombre) === norm(cap.capitulo_nombre));
                                const conceptosDisponibles = (capBD?.conceptos || []).filter(c => c.activo !== false);
                                const conceptoActual = (pd.capitulo_conceptos || {})[cap.id] || '';
                                // Saldo disponible del capítulo = Presupuesto − ejecutado (facturas).
                                // Presupuesto = Valor / 1.15 (se le quita el 15% de AIU),
                                // igual que en Control Presupuestal.
                                const presupuestoCap = (parseFloat(cap.subtotal) || 0) / 1.15;
                                const ejecutadoCap = parseFloat(cap.valor_ejecutado_total) || 0;
                                const disponibleCap = presupuestoCap - ejecutadoCap;
                                return (
                                <div key={cap.id} className={`fac-capitulo-wrap ${seleccionado ? 'selected' : ''}`}>
                                  <label className={`fac-capitulo-check ${seleccionado ? 'selected' : ''}`}>
                                    <input
                                      type="checkbox"
                                      checked={seleccionado}
                                      onChange={() => toggleCapitulo(pd.proyecto_id, cap.id)}
                                    />
                                    <span className="fac-capitulo-nombre">{cap.capitulo_nombre}</span>
                                    <span
                                      className="fac-capitulo-valor"
                                      style={disponibleCap <= 0 ? { color: '#dc2626' } : { color: '#15803d' }}
                                      title={`Presupuesto (valor − 15% AIU): ${formatMoney(presupuestoCap)} · Ejecutado: ${formatMoney(ejecutadoCap)}`}
                                    >
                                      {formatMoney(disponibleCap)}
                                    </span>
                                  </label>
                                  {seleccionado && (
                                    <>
                                      <select
                                        className="fac-capitulo-concepto"
                                        value={conceptoActual}
                                        onChange={(e) => {
                                          setConceptoCapitulo(pd.proyecto_id, cap.id, e.target.value);
                                          verificarConcepto(pd.proyecto_id, cap.id, e.target.value);
                                        }}
                                      >
                                        <option value="">{conceptosDisponibles.length === 0 ? 'Sin conceptos definidos' : 'Seleccionar concepto…'}</option>
                                        {conceptosDisponibles.map(c => (
                                          <option key={c.id} value={c.nombre}>{c.nombre}</option>
                                        ))}
                                      </select>
                                      {conceptoViolaciones[`${pd.proyecto_id}_${cap.id}`] && (
                                        <span style={{ color: '#dc2626', fontSize: '0.72rem', marginTop: 2 }}>
                                          ⚠️ Este concepto ya fue usado en este capítulo para este proyecto
                                        </span>
                                      )}
                                    </>
                                  )}
                                </div>
                                );
                              })}
                            </div>
                          </div>
                          );
                        })()}

                        {/* Payment plan per project */}
                        {pd.plan_pagos && (
                          <div className="fac-plan-section">
                            <div className="fac-plan-header">
                              <span>Cuotas</span>
                              <button type="button" className="fac-btn fac-btn-sm fac-btn-secondary" onClick={() => agregarCuota(pd.proyecto_id)}>
                                + Cuota
                              </button>
                            </div>
                            {pd.cuotas.map((cuota, i) => {
                              // Conceptos disponibles: solo los de los capítulos seleccionados de ESTE proyecto
                              const norm = (s) => (s || '').toString().normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim().replace(/\s+/g, ' ');
                              const capsSeleccionados = (pd.capitulos_seleccionados || []);
                              const nombresCapsSel = (pd.capitulos || [])
                                .filter(c => capsSeleccionados.includes(c.id))
                                .map(c => norm(c.capitulo_nombre));
                              // Si hay capítulos seleccionados → solo conceptos de esos capítulos. Si no → todos agrupados.
                              const capsBDFiltrados = nombresCapsSel.length > 0
                                ? conceptosBD.filter(cap => nombresCapsSel.includes(norm(cap.nombre)))
                                : conceptosBD;
                              return (
                              <div key={i} className="fac-cuota-row">
                                <span className="fac-cuota-num">#{i + 1}</span>
                                <select
                                  value={cuota.concepto || ''}
                                  onChange={e => actualizarCuota(pd.proyecto_id, i, 'concepto', e.target.value)}
                                  className="fac-cuota-concepto"
                                >
                                  <option value="">Concepto...</option>
                                  {capsBDFiltrados.length === 0 ? (
                                    CONCEPTOS_FACTURA.map(c => <option key={c.value} value={c.value}>{c.label}</option>)
                                  ) : (
                                    capsBDFiltrados.map(cap => (
                                      <optgroup key={cap.id} label={cap.nombre}>
                                        {(cap.conceptos || []).filter(c => c.activo !== false).map(c => (
                                          <option key={c.id} value={c.nombre}>{c.nombre}</option>
                                        ))}
                                      </optgroup>
                                    ))
                                  )}
                                </select>
                                <input
                                  type="date"
                                  value={cuota.fecha_vencimiento}
                                  onChange={e => actualizarCuota(pd.proyecto_id, i, 'fecha_vencimiento', e.target.value)}
                                />
                                <input
                                  type="number"
                                  value={cuota.monto}
                                  placeholder="Monto"
                                  onChange={e => actualizarCuota(pd.proyecto_id, i, 'monto', e.target.value)}
                                  min="0"
                                />
                                {pd.cuotas.length > 1 && (
                                  <button type="button" className="fac-btn-icon fac-btn-danger" onClick={() => quitarCuota(pd.proyecto_id, i)}>X</button>
                                )}
                              </div>
                              );
                            })}
                            {pd.cuotas.length > 0 && (
                              <div className={`fac-plan-total ${Math.abs(totalCuotas - (parseFloat(pd.valor) || 0)) > 1 ? 'fac-plan-warn' : ''}`}>
                                Total cuotas: {formatMoney(totalCuotas)} / {formatMoney(parseFloat(pd.valor) || 0)}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* Distribution total */}
                  {proyectosDistribucion.length > 0 && parseFloat(formData.subtotal) > 0 && (
                    <div className={`fac-dist-total ${
                      Math.abs(proyectosDistribucion.reduce((s, p) => s + (parseFloat(p.valor) || 0), 0) - parseFloat(formData.subtotal)) > 1
                        ? 'fac-dist-warn' : 'fac-dist-ok'
                    }`}>
                      Distribuido: {formatMoney(proyectosDistribucion.reduce((s, p) => s + (parseFloat(p.valor) || 0), 0))}
                      {' / '} Subtotal: {formatMoney(parseFloat(formData.subtotal))}
                    </div>
                  )}
                </div>
                )}

                {/* Descuentos pactados con este proveedor que aún tienen saldo */}
                {descDisponibles.length > 0 && (
                  <div className="fd-aviso">
                    <div className="fd-aviso-tit">
                      Este proveedor tiene {descDisponibles.length} descuento{descDisponibles.length > 1 ? 's' : ''} con saldo
                    </div>
                    <div className="fd-aviso-sub">
                      Marca los que quieras restarle a esta factura y escribe cuánto aplicar. Si dejas
                      el máximo y el descuento es mayor que la factura, se usa lo que alcance y el
                      resto queda para la siguiente.
                    </div>
                    <div className="fd-lista">
                      {descDisponibles.map(d => {
                        const activa = descSeleccionados.includes(d.id);
                        return (
                          <label key={d.id} className={`fd-op ${activa ? 'activa' : ''}`}>
                            <input
                              type="checkbox"
                              checked={activa}
                              onChange={() => setDescSeleccionados(prev =>
                                prev.includes(d.id) ? prev.filter(x => x !== d.id) : [...prev, d.id])}
                            />
                            <div className="fd-op-txt">
                              <div className="fd-op-motivo">{d.motivo}</div>
                              <div className="fd-op-meta">
                                {d.fecha} · {d.proyecto_nombre}{d.apartamento ? ` ${d.apartamento}` : ''} · {d.capitulo_nombre}
                              </div>
                            </div>
                            <span className="fd-op-saldo">${Math.round(d.saldo).toLocaleString('es-CO')}</span>
                            {/* Cuánto de este descuento va a ESTA factura (al crear y al
                                editar): así se reparte entre varias facturas o entre las
                                cuotas de un plan de pagos. Por defecto, lo que alcance. */}
                            {activa && (
                              <span className="fd-monto" onClick={e => e.preventDefault()}>
                                <label>Aplicar</label>
                                <input
                                  type="number" min="0" max={topeDescuento(d)} step="1"
                                  value={descMontos[d.id] !== undefined ? descMontos[d.id] : topeDescuento(d)}
                                  onClick={e => { e.stopPropagation(); e.preventDefault(); }}
                                  onChange={e => setDescMontos(prev => ({ ...prev, [d.id]: e.target.value }))}
                                />
                                <small>máx ${topeDescuento(d).toLocaleString('es-CO')}{capacidadDescuento <= 0 ? ' (escribe primero el total de la factura)' : ''}</small>
                              </span>
                            )}
                          </label>
                        );
                      })}
                    </div>
                    {descSeleccionados.length > 0 && (
                      <div className="fd-total">
                        Se descontará{' '}
                        <strong>
                          ${Math.round(descDisponibles
                            .filter(d => descSeleccionados.includes(d.id))
                            .reduce((s, d) => s + montoDescuento(d), 0)).toLocaleString('es-CO')}
                        </strong>{' '}
                        de esta factura.
                      </div>
                    )}
                  </div>
                )}

                {/* File upload */}
                <div className="fac-form-row">
                  <div className="fac-form-group fac-form-grow">
                    <label>Documento adjunto {!facturaEditandoId ? '*' : ''}</label>
                    {facturaEditandoId && archivoExistente && !archivoAdjunto ? (
                      <div className="fac-archivo-preview">
                        📎 {archivoExistente}
                        <button type="button" onClick={() => setArchivoExistente(null)}>Cambiar</button>
                      </div>
                    ) : (
                      <input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
                        onChange={e => setArchivoAdjunto(e.target.files[0] || null)}
                      />
                    )}
                    {archivoAdjunto && (
                      <div className="fac-archivo-preview">
                        {archivoAdjunto.name}
                        <button type="button" onClick={() => setArchivoAdjunto(null)}>X</button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Form actions */}
              <div className="fac-form-actions">
                <button type="button" className="fac-btn fac-btn-secondary" onClick={() => { setMostrarFormulario(false); resetFormulario(); }}>
                  Cancelar
                </button>
                <button type="submit" className="fac-btn fac-btn-primary" disabled={guardando || (facturaExiste && !facturaEditandoId)}>
                  {guardando ? 'Guardando...' : (facturaEditandoId ? 'Guardar Cambios' : 'Crear Factura')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Facturas;
